import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { injectInfiniteQuery, injectMutation, injectQuery, injectQueryClient } from './query'
import { Query, QueryObserverResult } from '@tanstack/query-core'
import { catchError, firstValueFrom, forkJoin, map, mergeMap, Observable, of, tap } from 'rxjs'
import { format as formatDate } from 'date-fns'
import type { CitationMinuteNodes } from '../../../backend/src/citations/minutes-citations-api.dto'
import { DemoProfilesService } from './demo-profiles.service'
import { DownloadsService } from './downloads.service'
import { CallFilters } from './filters/filters.component'
import { GuestOrganization } from './guest-organizations.service'
import { ListParameterQueryOptional } from './list-query-options'
import { LoggerService } from './logger.service'
import { MetricsService } from './metrics.service'
import { Organization } from './organizations.service'
import { queryKeys } from './query-keys'
import { MINIMUM_SEGMENT_DURATION } from './speaker-x-ray/speaker-x-ray.component'
import { ToastService } from './toast.service'
import { User } from './users.service'
import { MinuteTimelineSegment, TemplateContentBlockSourceType, TemplateContentBlockType } from './template-editor.component/types/template.types'
import { DocumentUploadInfo, generateFileName, generateHeadersWithTimezone, qualifyFileName } from './utils'
import { DateRange } from './common/date-range-picker/date-range-picker.component'
import { CallProcessingStatus, CallSessionStatus, CallStatus, CallType } from './models/call-enums'
import type { BatchJobType, CallProvider, ConsentResponse } from './models/call-enums'

export { CallProcessingStatus, CallSessionStatus, CallStatus, CallType } from './models/call-enums'
export type { BatchJobType, CallProvider, ConsentResponse } from './models/call-enums'

const ONGOING_CALLS_FAST_POLL_DURATION_MS = 15_000

export function anyFiltersApplied(filters: CallFilters, includeQuery = true): boolean {
  const isQueryActive = includeQuery ? filters.query !== null && filters.query !== '' : false
  return (
    isQueryActive ||
    filters.guestOrganizationId !== null ||
    filters.attendeeId !== null ||
    filters.speakerIds.length > 0 ||
    filters.startDate !== null ||
    filters.endDate !== null
  )
}

export type ExtendResult = { status: 'EXTENDED' | 'NOT_EXTENDED' | 'ERROR'; leaveDate: string | null }
export type ExtendPreview = { status: 'EXTENDABLE' | 'NOT_EXTENDABLE' | 'ERROR'; leaveDate: string | null }

export type ClientCallUser = {
  userId: number
  firstName: string
  lastName: string
  email: string
  isSetToJoin: boolean
}

/**
 * The things a person may be to a meeting, in the order the dropdown offers them.
 *
 * `OWNER` is not a stored access level: an owner is a call user and the rest are share levels. The
 * backend resolves that split, so the dialog reads and writes one role per person.
 */
export const CALL_ACCESS_ROLES = ['OWNER', 'EDITOR', 'REVIEWER', 'COMMENTER', 'RESTRICTED'] as const

export type CallAccessRole = (typeof CALL_ACCESS_ROLES)[number]

/** The access levels a call may be shared at: Restricted only where its call, organization or client allows it. */
export function filterOfferedAccessLevels<T extends string>(levels: readonly T[], allowRestrictedUsers: boolean): T[] {
  return levels.filter((level) => allowRestrictedUsers || level !== 'RESTRICTED')
}

export type ClientCallAccess = {
  userId: number
  firstName: string
  lastName: string
  email: string
  role: CallAccessRole
  isSetToJoin: boolean
}

// Restated rather than taken from ClientCallDto: that file names the Prisma enums, which the UI
// build cannot resolve, and the types shared across the boundary are import-free by convention.
/** A meeting's status as the admin meetings page reports and filters it, rather than the pipeline's own. */
export type CallReportingStatus = 'SCHEDULED' | 'NOT_JOINING' | 'RECORDING' | 'PROCESSING' | 'RECORDED' | 'NOT_RECORDED' | 'CANCELLED' | 'SOFT_DELETED'

export type ClientCall = {
  id: number
  name: string
  organizationId: number
  organizationName: string
  date: Date
  endDate: Date
  duration: number | null
  status: CallStatus
  reportingStatus: CallReportingStatus
  type: CallType
  latestCallSessionStatus: CallSessionStatus | null
  processingStatus: CallProcessingStatus | null
  users: ClientCallUser[]
  access: ClientCallAccess[]
  markedToBeDeletedAt: Date | null
  deletionDate: Date | null
  isOngoing: boolean
  isCompleted: boolean
  actualEndDate: Date | null
  isEligibleForRecoveryAssistant: boolean
  canActOnCall: boolean
  allowRestrictedUsers: boolean
  canExtendRetentionPeriod: boolean
  softDeletionDate: Date | null
  maximumDeletionDate: Date | null
  maximumSoftDeletionDate: Date | null
  maximumSoftDeletionDateInTotal: Date | null
  maxSingleExtensionInDays: number | null
  maxTotalExtensionInDays: number | null
  usedTotalExtensionInDays: number
  minimumSoftDeletionDate: Date | null
  minimumDeletionDate: Date | null
  retentionExtensionCount: number
}

export type ClientCallQueryResult = { list: ClientCall[]; total: number }

export type CallRetentionExtension = {
  id: number
  createdAt: Date
  extendedByName: string
  /** Both null on an extension an administrator made on their own initiative rather than on request. */
  requestedByName: string | null
  requestedAt: Date | null
  deletionDate: Date
  previousDeletionDate: Date | null
  /** Both null on the entries written before the two deletion dates could be moved apart. */
  softDeletionDate: Date | null
  previousSoftDeletionDate: Date | null
}

export type PendingRetentionExtensionRequest = {
  id: number
  createdAt: Date
  requestedByName: string
  extensionInDays: number
}

export type RetentionExtensionRequestOptions = {
  isEnabled: boolean
  availableExtensionsInDays: number[]
  pendingRequest: PendingRetentionExtensionRequest | null
}

export type RetentionExtensionRequestStatus = 'PENDING' | 'APPROVED' | 'DECLINED'

export type RetentionExtensionRequestReview = {
  id: number
  callId: number
  /** Null for a support user looking at another client's meeting. */
  callName: string | null
  callDate: Date
  createdAt: Date
  requestedByName: string
  extensionInDays: number
  status: RetentionExtensionRequestStatus
  decidedAt: Date | null
  decidedByName: string | null
  isCallSoftDeleted: boolean
  currentSoftDeletionDate: Date | null
  resultingSoftDeletionDate: Date | null
}

/** The extensions a meeting user may ask for, in the order the dialog offers them. */
export const REQUESTABLE_EXTENSIONS_IN_DAYS = [1, 3, 5, 7]

export type PastCallsType = 'ALL' | 'OWN_CALLS' | 'SHARED_WITH_ME'
export type CallListType = 'upcoming' | 'past' | 'upcoming-search' | 'past-search'
export type AgendaType = 'SINGLE' | 'MULTIPLE'
export type Sentence = {
  sentenceId: string
  content: string
  startTimeSeconds: number
  endTimeSeconds: number
}

export type TranscriptSpeakerSlot = {
  type: 'speaker'
  speaker: Speaker
  startTime: number
  sentences: Sentence[]
  duration: number
}

export type TranscriptPauseSlot = {
  type: 'pause'
  startTime: number
  endTime: number
  actualDuration: number
}

export type TranscriptSlot = TranscriptSpeakerSlot | TranscriptPauseSlot

export type Transcript = TranscriptSlot[]

export type ActionItem = {
  id: number
  item: string
  assignee: string | null
  deadline: string | null
}

export type Challenge = {
  id: number
  item: string
  reason: string | null
  person: string | null
}

export type OutlineItem = {
  id: number
  title: string
  startTimeSeconds: number
  startSentenceId: string
  endSentenceId: string
  discussion: string
  actionItems: ActionItem[]
  challenges: Challenge[]
  decisions: string[]
}

export type SearchMatch = {
  sentence: string
  sentenceSearchId: string
  speakerId: string
  speakerName: string | null
  startTimeSeconds: number
}

export type CallAccessLevel = 'COMMENTER' | 'REVIEWER' | 'EDITOR' | 'RESTRICTED' | 'FULL_ACCESS'
export type CallAttendanceType = 'INTERNAL' | 'EXTERNAL'

export type CallShare = {
  sharedUser: User
  sharedUserId: number
  level: CallAccessLevel
}

export type Speaker = {
  speakerId: string
  name: string | null
  speakerTag: string
  startTimeSeconds: number
  originalTag: string | null
}

export type MeetingRoomScore = {
  roomName: string
  averageConfidenceScore: number | null
  averageRms: number
  averageHfRatio: number
  totalSpeakingTimeSeconds: number
  physicalSpeakerTags: string[]
}

export type RecentInteraction = {
  id: number
  date: Date
  provider: CallProvider
  callName: string
}

export type MeetingInviteStatus = 'ACCEPT' | 'TENTATIVE' | 'DECLINE'

export type GroupAttendee = {
  email: string
  name: string
  response: MeetingInviteStatus
  isOrganizer: boolean
}

export type GroupedAttendeeOrganization = {
  organizationName: string
  attendees: GroupAttendee[]
}

export type CallResourceType =
  | 'USER_UPLOADED_VIDEO'
  | 'USER_UPLOADED_AUDIO'
  | 'BOT_RECORDED_VIDEO'
  | 'BOT_RECORDED_AUDIO'
  | 'DISPLAY_VIDEO'
  | 'DISPLAY_AUDIO'
  | 'PROCESSED_AUDIO'
  | 'RESIZED_THUMBNAIL'
  | 'ORIGINAL_THUMBNAIL'
  | 'HALO_SPEAKERS'
  | 'AGENDA'
  | 'CUSTOM_DOCUMENT'
  | 'BOARD_PACK'

export type CallResource = {
  id: number
  createdAt: string
  updatedAt: string
  callId: number
  resourceType: CallResourceType
  fileName: string
  extension: string
  cloudStorageFileName: string
  index: number
  deletedAt: string | null
  downloadUrl: string | null
  deletionReason: string | null
  uploadedByUser: boolean
  uploadedByIngestBoardPackJob: boolean
  size: number
  metadata: CallResourceMetadata | null
}

export type CallResourceWithUrl = CallResource & {
  downloadUrl: string | null
}

export type CallResourceMetadata = {
  numberOfPages: number
  tokenCount: number | null
}

export type AdminCallResourceMetadata = {
  numberOfPages: number
  tokenCount: number | null
  hasDescription: boolean
  keywordCount: number
}

export type AdminCallResource = Omit<CallResource, 'metadata' | 'fileName'> & {
  fileName: string | null
  metadata: AdminCallResourceMetadata | null
}

export type PauseInterval = {
  start: number
  end: number
  actualDuration: number
}

export type RecordingClockAnchor = {
  recordingPosition: number
  date: string
}

export type SpeakerMapping = { [key: string]: string }
export type MinutesGenerationTemplateProgress = { level: string; index: number; total: number }

export type SendRecoveryBotResult = {
  outcome: 'APPENDED' | ResendBotResult['outcome']
}

const SEND_RECOVERY_BOT_TOASTS: Record<SendRecoveryBotResult['outcome'], string> = {
  APPENDED: 'CALLS.ADMIN.SEND_RECOVERY_BOT.SUCCESS',
  REQUEUED: 'CALLS.RESEND_BOT.SUCCESS',
  ALREADY_ON_ITS_WAY: 'CALLS.RESEND_BOT.ALREADY_ON_ITS_WAY',
}

export type ResendBotResult = {
  outcome: 'REQUEUED' | 'ALREADY_ON_ITS_WAY'
}

/** One request to see a meeting outside the requester's own organization, and how it was settled. */
export type CallAccessRequest = {
  id: number
  callId: number
  requestedByUserId: number
  requestedAt: Date | null
  grantedByUserId: number | null
  grantedAt: Date | null
  revokedByUserId: number | null
  revokedAt: Date | null
}

export type Call = {
  transcript: Transcript
  id: number
  name: string
  allowTranscriptDownload: boolean
  allowMinutesDownload: boolean
  allowOutlineDownload: boolean
  organizationId: number
  organizationName: string
  provider: string
  description: string | null
  status: CallStatus
  endReason: string | null
  errorReason: string | null
  context: string[]
  date: Date
  endDate: Date
  actualStartDate: Date
  actualEndDate: Date
  queuedAt: Date
  waitingToBeAdmittedAt: Date
  createdAt: Date
  duration: number | null
  thumbnailUrl: string | null
  botInstructedToJoinByOtherUsers: boolean
  videoUrl: string | null
  audioUrl: string | null
  isPrevious: boolean
  isOngoing: boolean
  isUpcoming: boolean
  isMinutesGenerationOngoing: boolean
  /**
   * Whether a regeneration of the minutes is under way, including while a mapping-first run is still waiting on
   * its agenda-to-transcript mapping. `isMinutesGenerationOngoing` covers only the minutes job itself, which
   * reads as not started for that whole wait.
   */
  isMinutesRegenerationInFlight: boolean
  minutesGenerationProgress: number | null
  minutesGenerationTemplateProgress: MinutesGenerationTemplateProgress | null
  /**
   * The template items an in-flight regeneration is rewriting, including while it is still waiting on the
   * agenda-to-transcript mapping that precedes it. The editor locks their agenda items and leaves the rest of
   * the minutes editable. Null when nothing is running, or when the run covers the whole template.
   */
  minutesGenerationTargetTemplateItemIds: number[] | null
  hasMinutes: boolean
  hasErrorEmailBeenSent: boolean
  hasSoftDeletionWarningEmailBeenSent: boolean
  hasSoftDeletionConfirmationEmailBeenSent: boolean
  hasUpcomingCallReminderEmailBeenSent: boolean
  hasNativeFallbackEmailBeenSent: boolean
  transcriptTokenCount: number | null
  matches?: SearchMatch[] // TODO Lazy typing as the types for search and normal call showing are different...
  accessLevel: CallAccessLevel
  attendanceType: CallAttendanceType
  isSetToJoin: boolean
  notifyWhenPostProcessingCompletes: boolean
  userSpeakerMapping: SpeakerMapping | null
  automatedSpeakerMapping: SpeakerMapping | null
  processingStatus: CallProcessingStatus | null
  processingProgress: number | null
  hangUpKey: string | null
  haveSpeakersBeenUpdated: boolean
  isMinutingUpToDate: boolean
  isOutlineUpToDate: boolean
  usePartialMinutesRegeneration: boolean
  hasRestrictedMinuteItems: boolean
  /** Whether the call may be shared with Restricted access, resolved through the call, organization and client. */
  allowRestrictedUsers: boolean
  allowCallEntities: boolean
  allowActionItems: boolean
  counterpartyNames: string[]
  currentParticipantCount: number | null
  link: string | null
  isSpeechMissing: boolean
  isPostProcessingComplete: boolean
  isTranscriptionComplete: boolean
  allowMediaDownload: boolean
  markedToBeDeletedAt: Date | null
  calendarSyncLostAt: Date | null
  isTranscriptReady: boolean
  isMediaReady: boolean
  isOutlineReady: boolean
  physicalSpeakersExist: boolean
  type: CallType
  medium: CallMedium
  attendeeNames: string[]
  hasVirtualSpeakers: boolean
  participantCount: number
  callProfileId: number | null
  hasWarningForMissingAgendaBeenShown: boolean
  haveMinuteStylesBeenUpdatedSinceLastProcessing: boolean
  totalNumberOfSplits: number
  templateId: number | null
  callSetIndex: number | null
  callSetSize: number | null
  finalDeletionDate: Date | null
  retentionPeriodExpirationDate: Date | null
  retentionPeriodInDaysForCall: number | null
  mediaDeletionDate: Date | null
  documentDeletionDate: Date | null
  transcriptDeletionDate: Date | null
  avgConfidenceScore: number | null

  outlineItems: OutlineItem[]
  shares: CallShare[]
  users: User[]
  speakers: Speaker[]
  recentInteractions: RecentInteraction[]
  groupedAttendees: GroupedAttendeeOrganization[]

  agendaDownloadUrl: string | null
  userUploadedResources: CallResourceWithUrl[]
  uploadedRecording: CallResource | null
  agendaType: AgendaType
  templateSourceType: TemplateSourceType | null
  pauseIntervals: PauseInterval[]
  recordingClockAnchors: RecordingClockAnchor[] | null
  hasEditorDocument: boolean
  hasOnlyLegacyMinutes: boolean

  minutesCitations: CitationMinuteNodes | null
  agendaMetadata: AdminAgendaMetadata
  agendaExtractionProgress: number | null
  agendaExtractionFailed: boolean
  invalidAgendaReason: InvalidAgendaReason | null
  isCheckingAgendaValidity: boolean
  hasTemplateItems: boolean
  isAgendaExtractionInProgress: boolean
  isIngestingBoardPack: boolean
  boardPackIngestionProgress: number | null
  boardPackIngestionFailed: boolean
  templateApprovedByUser: boolean
  isParticipantExtractionInProgress: boolean
  lastSpeechEndTime: number | null
  transcriptLanguage: string | null
  allowSystemGuidanceEditing: boolean
} & MinutesStyling

export type CallWithLatestSession = Call & {
  latestCallSession: CallSession | null
}

export type CallTimelineEvent = {
  type: string
  timestamp: Date
  userName: string | null
  metadata: any | null
}

export type CallTimelineUserActivity = {
  userName: string
  hasEnabledAssistant: boolean
  viewCount: number
  lastViewedAt: Date | null
  lastEnabledAt: Date | null
  lastDisabledAt: Date | null
}

export type CallTimelineResponse = {
  events: CallTimelineEvent[]
  userActivities: CallTimelineUserActivity[]
}

export type AdminOutlineStructureItem = {
  id: number
  actionItemCount: number
  challengeCount: number
  decisionCount: number
  startTimeSeconds: number
  hasError: boolean
}

export type AdminOutlineStructure = {
  items: AdminOutlineStructureItem[]
}

export enum AgendaAttendeeType {
  CHAIR = 'CHAIR',
  MEMBER = 'MEMBER',
  STANDING_ATTENDEE = 'STANDING_ATTENDEE',
  ATTENDEE_BY_INVITATION = 'ATTENDEE_BY_INVITATION',
  OBSERVER = 'OBSERVER',
  COMPANY_SECRETARY = 'COMPANY_SECRETARY',
  APOLOGY = 'APOLOGY',
  OTHER = 'OTHER',
}

export enum GoverningBodyType {
  BOARD = 'BOARD',
  COMMITTEE = 'COMMITTEE',
  OTHER = 'OTHER',
}

export enum TemplateSourceType {
  UPLOAD = 'UPLOAD',
  CREATE = 'CREATE',
  LOAD = 'LOAD',
}

export function computeIsCheckingAgendaValidity(call: Pick<Call, 'isCheckingAgendaValidity'>): boolean {
  return call.isCheckingAgendaValidity
}

export function isAgendaExtractionRejectionVisible(call: Pick<Call, 'invalidAgendaReason' | 'agendaExtractionFailed' | 'templateSourceType'>): boolean {
  return (call.invalidAgendaReason !== null || call.agendaExtractionFailed) && call.templateSourceType === TemplateSourceType.UPLOAD
}

export type AgendaUploadStatus = 'idle' | 'uploading' | 'checking' | 'failed' | 'uploaded'

export function computeAgendaUploadStatus(params: { isImporting: boolean; isFailed: boolean; isChecking: boolean; isUploaded: boolean }): AgendaUploadStatus {
  if (params.isImporting) {
    return 'uploading'
  }
  if (params.isFailed) {
    return 'failed'
  }
  if (params.isChecking) {
    return 'checking'
  }
  if (params.isUploaded) {
    return 'uploaded'
  }
  return 'idle'
}

export function computeAgendaFailureMessageKey(invalidAgendaReason: InvalidAgendaReason | null, agendaExtractionFailed: boolean): string | null {
  if (invalidAgendaReason !== null) {
    return `CALL_SETUP.TEMPLATE_REVIEW.INVALID_AGENDA_REASONS.${invalidAgendaReason}`
  }
  if (agendaExtractionFailed) {
    return 'CALL_SETUP.TEMPLATE_REVIEW.EXTRACTION_FAILED'
  }
  return null
}

export function computeHasUsableTemplateSource(params: {
  hasAgendaUploaded: boolean
  templateSourceType: TemplateSourceType | null
  isCheckingAgendaValidity: boolean
  isImportingAgenda: boolean
  isAgendaExtractionFailed: boolean
}): boolean {
  const hasTemplateSource = params.hasAgendaUploaded || params.templateSourceType !== null
  if (!hasTemplateSource) {
    return false
  }
  return !params.isCheckingAgendaValidity && !params.isImportingAgenda && !params.isAgendaExtractionFailed
}

export function computeShouldConfirmTemplateReplace(params: {
  templateSourceType: TemplateSourceType | null
  isCheckingAgendaValidity: boolean
  isImportingAgenda: boolean
  isAgendaExtractionFailed: boolean
  isCreateTemplateSelected: boolean
  templateItemsCount: number
}): boolean {
  if (params.templateSourceType === null) {
    return false
  }
  if (params.isCheckingAgendaValidity || params.isImportingAgenda || params.isAgendaExtractionFailed) {
    return false
  }
  if (params.isCreateTemplateSelected && params.templateItemsCount === 0) {
    return false
  }
  return true
}

export function computeShouldShowTemplateSourcePicker(params: {
  isCheckingAgendaValidity: boolean
  isAgendaExtractionFailed: boolean
  hasTemplateItems: boolean
  itemCount: number
  templateSourceType: TemplateSourceType | null
}): boolean {
  if (params.isCheckingAgendaValidity) {
    return true
  }
  if (params.isAgendaExtractionFailed && !params.hasTemplateItems) {
    return true
  }
  return params.itemCount === 0 && params.templateSourceType === null
}

export function computeCurrentStep(params: { isUploadedCall: boolean; isMultipleAgenda: boolean; hasUsableTemplateSource: boolean }): number {
  if (params.isUploadedCall) {
    return params.hasUsableTemplateSource ? 4 : 2
  }
  if (params.isMultipleAgenda) {
    return 3
  }
  return params.hasUsableTemplateSource ? 4 : 3
}

export function computeIsTemplateReviewVisible(params: {
  isMultipleAgenda: boolean
  currentStep: number
  reviewStepNumber: number
  isPopulatingTemplate: boolean
  isIngestingBoardPack: boolean
  isImportingAgenda: boolean
  isAgendaExtractionInProgress: boolean
  hasCall: boolean
  hasMetadata: boolean
}): boolean {
  const hasReachedReviewStep = params.currentStep >= params.reviewStepNumber && !params.isAgendaExtractionInProgress
  return (
    !params.isMultipleAgenda &&
    !params.isImportingAgenda &&
    (hasReachedReviewStep || params.isPopulatingTemplate || params.isIngestingBoardPack) &&
    params.hasCall &&
    params.hasMetadata
  )
}

export type AdminAgendaAttendee = {
  id: number
  firstName: string
  lastName: string
  role: string | null
  entity: string | null
  type: AgendaAttendeeType
  initials: string | null
  honorifics: string | null
}

export type AdminAgendaMetadata = {
  id: number
  companyName: string | null
  location: string | null
  date: string | null
  time: string | null
  entities: string[]
  committeeType: string | null
  governingBodyType: GoverningBodyType | null
  attendees: AdminAgendaAttendee[]
}

export type CallUser = {
  user: User
  isSetToJoin: boolean
  isSetToJoinFrozenByUser: boolean
  notifyWhenPostProcessingCompletes: boolean
  hasWeekendDeletionReminderEmailBeenSent: boolean
  isPrimaryCalendar: boolean
}

export type CallSession = {
  index: number
  actualStartDate: Date | null
  actualEndDate: Date | null
  status: CallSessionStatus
  duration: number | null
  errorReason: string | null
  endReason: string | null
  botErrorReason: string | null
  waitingToBeAdmittedAt: Date | null
  lastHeartbeatReceivedAt: Date | null
  pauseIntervals: PauseInterval[]
  originalSplitOffsets: number[]
  adjustedSplitOffsets: number[]
  cloudRunExecutionName: string | null
  invoker: string | null
  useNativeBot: boolean
}

export type CallSessionWithSegments = CallSession & {
  segments: CallSegment[]
}

export type CallSegment = {
  callId: number
  sessionIndex: number
  index: number
  startOffset: number
  duration: number
}

export type AnonymizedCallParticipation = {
  joinedAt: Date
  leftAt: Date | null
}

export type AnonymizedCallParticipant = {
  id: number
  consentResponse: ConsentResponse | null
  consentRequestedAt: Date | null
  consentRespondedAt: Date | null
  participations: AnonymizedCallParticipation[]
}

export type InvalidAgendaReason = 'NO_READABLE_TEXT' | 'NO_AGENDA_ITEMS' | 'BOARD_PACK' | 'MINUTES_NOT_AGENDA' | 'OTHER'

export type CallBatchJobRun = {
  id: number
  type: BatchJobType
  task: string | null
  registeredAt: Date | null
  responseAt: Date | null
  successful: boolean | null
  error: string | null
  metadata: any
  callId: number
  completionPercentage: number
  executionGroupId: string
  triggeredBy: string
  isDry: boolean
  targetTemplateItemIds: number[]
}

export type CallBatchJobRunWithDuration = CallBatchJobRun & {
  duration: number
  agendaTranscriptMapping?: AgendaTranscriptMappingResponse | null
}

export type CallBatchJobDetails = {
  singleRuns: CallBatchJobRunWithDuration[]
}

export type InProgressBatchJob = CallBatchJobRunWithDuration & {
  organizationName: string
}

export type UploadedDocumentInfo = {
  resourceType: 'AGENDA' | 'CUSTOM_DOCUMENT' | 'BOARD_PACK'
  fileName: string
  size: number
}

export type DebugDataFile = {
  key: string
  fileName: string
  url: string
  lastModified: string
  mediaType: 'VIDEO' | 'IMAGE' | 'OTHER'
}

export type EditorDebugRevision = {
  id: number
  userId: number | null
  hasContent: boolean
  hasContentRaw: boolean
  createdAt: string
  updatedAt: string
}

export type EditorDebugLoadingInputs = {
  isPostProcessingComplete: boolean
  isMinutesGenerationOngoing: boolean
  isMinutesGenerationTargeted: boolean
  minutesGenerationProgress: number | null
  isSpeechMissing: boolean
}

export type EditorDebugSyncReadiness = {
  hasPersistedYDocument: boolean
  hasAnyRevision: boolean
}

export type EditorAdminDebug = {
  revisions: EditorDebugRevision[]
  loadingInputs: EditorDebugLoadingInputs
  syncReadiness: EditorDebugSyncReadiness
}

export type HallucinationAuditSegment = {
  start: number
  end: number
  avg_logprob: number | null
  word_count: number
  word_score_min: number | null
  word_score_mean: number | null
  text: string | null
  sentenceId: string | null
}

export type HallucinationAuditData = {
  callId: number
  jobId: number
  artifactMissing: boolean
  segments: HallucinationAuditSegment[]
}

export type AgendaTranscriptMappingSegment = {
  startSeconds: number
  endSeconds: number
  confidence: 'HIGH' | 'LOW'
}

export type AgendaTranscriptMappingItem = {
  agendaItemId: number | null
  itemMappingId: number | null
  agendaItemTitle?: string
  itemNumber: string
  depth: number
  topLevelAncestorId: number | null
  hasChildren: boolean
  contentBlockTypes: string[]
  orderIndex: number
  status: 'DISCUSSED' | 'NOT_DISCUSSED' | 'NO_DISCUSSION_FOUND'
  segments: AgendaTranscriptMappingSegment[]
}

export type AgendaTranscriptMappingUnmappedRegion = {
  startSeconds: number
  endSeconds: number
  kind: 'MAPPING_GAP' | 'SILENCE'
}

export type AgendaTranscriptMappingUnmappedDiscussionRange = {
  startSeconds: number
  endSeconds: number
}

export type UpdateAgendaTranscriptMappingItem = {
  itemMappingId: number
  status: 'DISCUSSED' | 'NOT_DISCUSSED' | 'NO_DISCUSSION_FOUND'
  segments: AgendaTranscriptMappingSegment[]
}

export type AgendaTranscriptMappingResponse = {
  callId: number
  transcriptDurationSeconds: number
  isManuallyEdited: boolean
  isDry: boolean
  agendaItems: AgendaTranscriptMappingItem[]
  unmappedDiscussionRanges: AgendaTranscriptMappingUnmappedDiscussionRange[]
  unmappedRegions: AgendaTranscriptMappingUnmappedRegion[]
}

export type AgendaTranscriptMappingMode = 'DISABLED' | 'ENABLED' | 'ENABLED_HIGH_ONLY'

export type MinutingAgent = 'CONDENSATION' | 'COLLOQUIALISM' | 'NAMING'

export const MINUTING_AGENTS: MinutingAgent[] = ['CONDENSATION', 'COLLOQUIALISM', 'NAMING']

// Agents that always run and cannot be toggled off in the UI. Additional agents
// added later stay optional and remain selectable.
export const MANDATORY_MINUTING_AGENTS: MinutingAgent[] = ['CONDENSATION', 'COLLOQUIALISM', 'NAMING']

export type AnonymizedTemplateContentBlockSource = {
  type: TemplateContentBlockSourceType
  id: number | null
  fileName: string | null
}

export type AnonymizedBinaryOption = {
  placeholderCount: number
}

export type AnonymizedTemplateContentBlock = {
  type: TemplateContentBlockType
  levelOfDetail: MinutesVerboseness
  anonymization: MinutesAnonymization
  hasGuidance: boolean
  sources: AnonymizedTemplateContentBlockSource[]
  placeholderCount: number | null
  binaryOptions: AnonymizedBinaryOption[] | null
}

export type AnonymizedTemplateItem = {
  id: number
  index: number
  title: string | null
  presenterCount: number
  duration: number | null
  isSectionHeading: boolean
  hasSemanticSummary: boolean
  hasKeyVocabulary: boolean
  contentBlocks: AnonymizedTemplateContentBlock[]
  minuteTimelineSegments: MinuteTimelineSegment[]
  hasMinuteContent: boolean
  subitems: AnonymizedTemplateItem[]
}

export type AdminCallDetails = Pick<
  Call,
  | 'id'
  | 'status'
  | 'isSetToJoin'
  | 'date'
  | 'endDate'
  | 'actualStartDate'
  | 'actualEndDate'
  | 'createdAt'
  | 'speakers'
  | 'provider'
  | 'type'
  | 'isOngoing'
  | 'shares'
  | 'duration'
  | 'processingStatus'
  | 'userSpeakerMapping'
  | 'automatedSpeakerMapping'
  | 'haveSpeakersBeenUpdated'
  | 'markedToBeDeletedAt'
  | 'calendarSyncLostAt'
  | 'medium'
  | 'isSpeechMissing'
  | 'isMediaReady'
  | 'isTranscriptReady'
  | 'isOutlineReady'
  | 'isPostProcessingComplete'
  | 'endReason'
  | 'errorReason'
  | 'participantCount'
  | 'currentParticipantCount'
  | 'callProfileId'
  | 'agendaType'
  | 'templateId'
  | 'finalDeletionDate'
  | 'retentionPeriodExpirationDate'
  | 'retentionPeriodInDaysForCall'
  | 'mediaDeletionDate'
  | 'documentDeletionDate'
  | 'transcriptDeletionDate'
  | 'lastSpeechEndTime'
  | 'hasErrorEmailBeenSent'
  | 'hasSoftDeletionWarningEmailBeenSent'
  | 'hasSoftDeletionConfirmationEmailBeenSent'
  | 'hasUpcomingCallReminderEmailBeenSent'
  | 'hasNativeFallbackEmailBeenSent'
> &
  MinutesStyling & {
    isOrganizedByThisOrganization: boolean
    attendeeCountsByGuestOrganization: { organizationName: string; attendeeCount: number; hasOrganizer: boolean }[]
    agendaAttendees: Pick<AdminAgendaAttendee, 'id' | 'type'>[]
    attendance: AnonymizedCallParticipant[]
    contextEntryCount: number
    outlineStructure: AdminOutlineStructure
    callUsers: CallUser[]
    batchJobRuns: CallBatchJobDetails
    speakersFileDownloadUrl: string | null
    spectrogramUrl: string | null
    allResources: AdminCallResource[]
    organization: Organization
    templateSourceType: TemplateSourceType | null
    anonymizedTemplateItems: AnonymizedTemplateItem[] | null
    useNativeBot: boolean | null
    minutingAgents: MinutingAgent[]
    overrideOrganizationLevelMinutingAgents: boolean
    useVerificationDiscovery: boolean | null
    allowSystemGuidanceEditing: boolean
    useAgendaTranscriptMapping: AgendaTranscriptMappingMode | null
    anonymiseMemberSpeakers: boolean | null
    useTranscriptRefinement: boolean | null
    allowRestrictedUsers: boolean | null
    isMinutingUpToDate: boolean
    isOutlineUpToDate: boolean
    isAgendaTranscriptMappingUpToDate: boolean
    callSessions: CallSessionWithSegments[]
    latestCallSession: CallSession | null
    uploadedDocuments: UploadedDocumentInfo[]
    audioMetrics: {
      speakerDiagnostics: {
        segmentMetrics: {
          speaker: string
          startTimeSeconds: number
          endTimeSeconds: number
          durationSeconds: number
          rms: number
          peak: number
          hfRatio: number
          silenceRatio: number
          meanConfidenceScore?: number
          index: number
        }[]
        speakerMetrics: {
          [speaker: string]: {
            speaker: string
            totalSpeakingTimeSeconds: number
            segmentCount: number
            avgRms: number
            avgPeak: number
            avgHfRatio: number
            avgSilenceRatio: number
            avgConfidenceScore?: number
            haloSpeakerName?: string | null
          }
        }
        meetingMetrics: {
          totalDurationSeconds: number
          totalSegments: number
          totalSpeakers: number
          segmentsPerMinute: number
          avgRms: number
          avgPeak: number
          avgSilenceRatio: number
          avgHfRatio: number
          avgConfidenceScore?: number
        }
      } | null
    }
    avgConfidenceScore: number | null
    avgRms: number | null
    meetingRoomScores: MeetingRoomScore[]
    virtualSpeakers: Speaker[] | null
    virtualSpeakerMapping: SpeakerMapping | null
    keyName: string | null
  }

export type SearchResponse = Call[]

export type CallListResponse = {
  calls: Call[]
  hasMore: boolean
  nextCursor?: number
}

export enum PostProcessor {
  TRANSCODER = 'TRANSCODER',
  ASR = 'ASR',
  LLM_SPEAKER_LABELING = 'LLM_SPEAKER_LABELING',
  OUTLINE = 'OUTLINE',
  AGENDA_EXTRACTION = 'AGENDA_EXTRACTION',
  MINUTES = 'MINUTES',
  TRANSCODER_SPLIT = 'TRANSCODER_SPLIT',
  MAP_AGENDA_TO_TRANSCRIPT = 'MAP_AGENDA_TO_TRANSCRIPT',
  INGEST_BOARD_PACK = 'INGEST_BOARD_PACK',
  REFINE_TRANSCRIPT = 'REFINE_TRANSCRIPT',
  ACTION_ITEMS = 'ACTION_ITEMS',
}

export type LlmModel =
  | 'ANTHROPIC_CLAUDE_4_5_OPUS'
  | 'ANTHROPIC_CLAUDE_4_5_HAIKU'
  | 'ANTHROPIC_CLAUDE_4_6_OPUS'
  | 'ANTHROPIC_CLAUDE_4_6_SONNET'
  | 'ANTHROPIC_CLAUDE_4_7_OPUS'
  | 'ANTHROPIC_CLAUDE_5_FABLE'
  | 'ANTHROPIC_CLAUDE_5_5_OPUS'
  | 'ANTHROPIC_CLAUDE_5_SONNET'
  | 'ANTHROPIC_CLAUDE_5_5_HAIKU'
  | 'GOOGLE_GEMINI_2_5_PRO'

export const LLM_MODELS: LlmModel[] = [
  'ANTHROPIC_CLAUDE_4_5_OPUS',
  'ANTHROPIC_CLAUDE_4_5_HAIKU',
  'ANTHROPIC_CLAUDE_4_6_OPUS',
  'ANTHROPIC_CLAUDE_4_6_SONNET',
  'ANTHROPIC_CLAUDE_4_7_OPUS',
  'ANTHROPIC_CLAUDE_5_FABLE',
  'ANTHROPIC_CLAUDE_5_5_OPUS',
  'ANTHROPIC_CLAUDE_5_SONNET',
  'ANTHROPIC_CLAUDE_5_5_HAIKU',
  'GOOGLE_GEMINI_2_5_PRO',
]

export type LlmEffort = 'LOW' | 'MEDIUM' | 'HIGH' | 'EXTRA_HIGH' | 'MAX'

export const LLM_EFFORTS: LlmEffort[] = ['LOW', 'MEDIUM', 'HIGH', 'EXTRA_HIGH', 'MAX']

type Attendee = {
  name: string
  email: string
}

export enum MinutesVerboseness {
  CONCISE = 'CONCISE',
  DETAILED = 'DETAILED',
  EXHAUSTIVE = 'EXHAUSTIVE',
}

export enum MinutesAnonymization {
  ALL = 'ALL',
  NONE = 'NONE',
}

export enum MinutesPassive {
  ACTIVE = 'ACTIVE',
  CONTEXTUAL = 'CONTEXTUAL',
  PASSIVE = 'PASSIVE',
}

export enum MinutesNameFormat {
  FIRST_NAME = 'FIRST_NAME',
  ROLE = 'ROLE',
  SURNAME = 'SURNAME',
  HONORIFIC_SURNAME = 'HONORIFIC_SURNAME',
  INITIALS = 'INITIALS',
  FULL_NAME = 'FULL_NAME',
  INITIAL_SURNAME = 'INITIAL_SURNAME',
}

export enum MinutesDateFormat {
  DD_MONTH_YYYY = 'DD_MONTH_YYYY',
  MONTH_DD_TH_YYYY = 'MONTH_DD_TH_YYYY',
  DD_MM_YYYY_DOT = 'DD_MM_YYYY_DOT',
  DD_MM_YYYY_SLASH = 'DD_MM_YYYY_SLASH',
}

export enum MinutesTimeFormat {
  TWENTY_FOUR_HOUR = 'TWENTY_FOUR_HOUR',
  TWELVE_HOUR = 'TWELVE_HOUR',
}

export enum MinutesLanguage {
  BRITISH = 'BRITISH',
  AMERICAN = 'AMERICAN',
  SPANISH = 'SPANISH',
  FRENCH = 'FRENCH',
}

export enum MinutesAttribution {
  OUTCOME = 'OUTCOME',
  COLLECTIVE = 'COLLECTIVE',
  CONTEXTUAL = 'CONTEXTUAL',
}

export enum CallMedium {
  PHYSICAL = 'PHYSICAL',
  VIRTUAL = 'VIRTUAL',
}

export enum MetricType {
  OPEN_APP = 'OPEN_APP',
  VIEW_CALL = 'VIEW_CALL',
  RUN_LLM_LIVE_ACTION = 'RUN_LLM_LIVE_ACTION',
  COMPLETE_LLM_LIVE_ACTION = 'COMPLETE_LLM_LIVE_ACTION',
  SAVE_EDITOR_CONTENT = 'SAVE_EDITOR_CONTENT',
  COMMENT_ON_EDITOR = 'COMMENT_ON_EDITOR',
  CONNECT_CALENDAR = 'CONNECT_CALENDAR',

  UPLOAD_MEETING = 'UPLOAD_MEETING',
  ADD_MANUAL_MEETING = 'ADD_MANUAL_MEETING',
  CLICK_ON_UPCOMING_MEETING = 'CLICK_ON_UPCOMING_MEETING',
  TOGGLE_ON_MEETING = 'TOGGLE_ON_MEETING',
  TOGGLE_OFF_MEETING = 'TOGGLE_OFF_MEETING',

  CLICK_ON_OUTLINE_TAB = 'CLICK_ON_OUTLINE_TAB',
  CLICK_ON_OUTLINE_ITEM_TO_EXPAND = 'CLICK_ON_OUTLINE_ITEM_TO_EXPAND',
  CLICK_ON_OUTLINE_ITEM_TO_GO_TO_TRANSCRIPT = 'CLICK_ON_OUTLINE_ITEM_TO_GO_TO_TRANSCRIPT',
  CLICK_ON_EXPAND_ALL_OUTLINE_ITEMS = 'CLICK_ON_EXPAND_ALL_OUTLINE_ITEMS',
  CLICK_ON_COLLAPSE_ALL_OUTLINE_ITEMS = 'CLICK_ON_COLLAPSE_ALL_OUTLINE_ITEMS',

  CHANGE_MINUTING_STYLES = 'CHANGE_MINUTING_STYLES',
  CHANGE_AGENDA_TYPE = 'CHANGE_AGENDA_TYPE',

  CLICK_LLM_LIVE_ACTION_INSERTION_OF_SUGGESTION = 'CLICK_LLM_LIVE_ACTION_INSERTION_OF_SUGGESTION',

  TOGGLE_REVISION_HISTORY = 'TOGGLE_REVISION_HISTORY',
  RESTORE_REVISION = 'RESTORE_REVISION',

  SEARCH_WITH_KEYWORD = 'SEARCH_WITH_KEYWORD',
  SEARCH_WITH_FILTER_BY_TYPE = 'SEARCH_WITH_FILTER_BY_TYPE',
  CLICK_ON_MEETING_IN_SEARCH_RESULTS = 'CLICK_ON_MEETING_IN_SEARCH_RESULTS',
  CLICK_ON_TEXT_SNIPPET_IN_SEARCH_RESULTS = 'CLICK_ON_TEXT_SNIPPET_IN_SEARCH_RESULTS',

  CLICK_ON_SPEAKERS_XRAY_BUTTON = 'CLICK_ON_SPEAKERS_XRAY_BUTTON',
  ASSIGN_SPEAKER_VIA_XRAY = 'ASSIGN_SPEAKER_VIA_XRAY',
  ASSIGN_SPEAKER_VIA_TRANSCRIPT_SELECTION = 'ASSIGN_SPEAKER_VIA_TRANSCRIPT_SELECTION',
  ENABLE_SPEAKER_LABELING_MODE = 'ENABLE_SPEAKER_LABELING_MODE',
  DISABLE_SPEAKER_LABELING_MODE = 'DISABLE_SPEAKER_LABELING_MODE',

  UPLOAD_RESOURCE = 'UPLOAD_RESOURCE',
  DELETE_RESOURCE = 'DELETE_RESOURCE',

  PAUSE_RECORDING = 'PAUSE_RECORDING',
  RESUME_RECORDING = 'RESUME_RECORDING',

  CLICK_TRANSCRIPT_TO_REPLAY_VIDEO = 'CLICK_TRANSCRIPT_TO_REPLAY_VIDEO',
  CLICK_SKIP_TO_NEXT_SPEAKER_BUTTON = 'CLICK_SKIP_TO_NEXT_SPEAKER_BUTTON',

  DOWNLOAD_VIDEO_RECORDING = 'DOWNLOAD_VIDEO_RECORDING',
  DOWNLOAD_AUDIO_RECORDING = 'DOWNLOAD_AUDIO_RECORDING',
  DOWNLOAD_TRANSCRIPT = 'DOWNLOAD_TRANSCRIPT',
  DOWNLOAD_MINUTES = 'DOWNLOAD_MINUTES',
  DOWNLOAD_OUTLINE = 'DOWNLOAD_OUTLINE',
  DOWNLOAD_ACTION_ITEMS = 'DOWNLOAD_ACTION_ITEMS',

  JOIN_MEETING_NOW = 'JOIN_MEETING_NOW',

  CREATE_MEETING = 'CREATE_MEETING',
  UPDATE_MEETING = 'UPDATE_MEETING',
  POSTPONE_MEETING = 'POSTPONE_MEETING',
  CANCEL_MEETING = 'CANCEL_MEETING',

  DELETE_MEETING_SOFT = 'DELETE_MEETING_SOFT',
  DELETE_MEETING_HARD = 'DELETE_MEETING_HARD',

  SHARE_MEETING_WITH_EXISTING_USER = 'SHARE_MEETING_WITH_EXISTING_USER',
  SHARE_MEETING_WITH_NEW_USER = 'SHARE_MEETING_WITH_NEW_USER',
  REPROCESS_OUTPUTS = 'REPROCESS_OUTPUTS',

  ADD_AGENDA_ITEM = 'ADD_AGENDA_ITEM',
  DELETE_AGENDA_ITEM = 'DELETE_AGENDA_ITEM',
  UPDATE_AGENDA_ITEM = 'UPDATE_AGENDA_ITEM',
  RESET_AGENDA_ITEMS = 'RESET_AGENDA_ITEMS',

  LOAD_TEMPLATE = 'LOAD_TEMPLATE',
  CREATE_BLANK_TEMPLATE = 'CREATE_BLANK_TEMPLATE',
  ADD_TEMPLATE_ITEM = 'ADD_TEMPLATE_ITEM',
  REMOVE_TEMPLATE_ITEM = 'REMOVE_TEMPLATE_ITEM',
  MOVE_TEMPLATE_ITEM = 'MOVE_TEMPLATE_ITEM',
  EDIT_TEMPLATE_ITEM = 'EDIT_TEMPLATE_ITEM',
  ATTACH_TEMPLATE_DOCUMENT = 'ATTACH_TEMPLATE_DOCUMENT',
  DETACH_TEMPLATE_DOCUMENT = 'DETACH_TEMPLATE_DOCUMENT',

  REGISTER_MEETING = 'REGISTER_MEETING',
  HANGUP_MEETING = 'HANGUP_MEETING',

  JOIN_MEETING = 'JOIN_MEETING',
  LEAVE_MEETING = 'LEAVE_MEETING',

  SEND_EMAIL = 'SEND_EMAIL',
  MARK_MEETING_AS_ERRORED = 'MARK_MEETING_AS_ERRORED',

  SEND_TO_TRANSCODING = 'SEND_TO_TRANSCODING',
  COMPLETE_TRANSCODING = 'COMPLETE_TRANSCODING',
  RECEIVE_TRANSCODER_AUDIO_READY = 'RECEIVE_TRANSCODER_AUDIO_READY',

  SEND_TO_TRANSCRIPTION = 'SEND_TO_TRANSCRIPTION',
  COMPLETE_TRANSCRIPTION = 'COMPLETE_TRANSCRIPTION',

  SEND_TO_OUTLINE_GENERATION = 'SEND_TO_OUTLINE_GENERATION',
  COMPLETE_OUTLINE_GENERATION = 'COMPLETE_OUTLINE_GENERATION',

  SEND_TO_MINUTES_GENERATION = 'SEND_TO_MINUTES_GENERATION',
  COMPLETE_MINUTES_GENERATION = 'COMPLETE_MINUTES_GENERATION',
  MINUTES_GENERATION_INPUT_TOKENS = 'MINUTES_GENERATION_INPUT_TOKENS',
  MINUTES_GENERATION_OUTPUT_TOKENS = 'MINUTES_GENERATION_OUTPUT_TOKENS',

  SEND_TO_AGENDA_EXTRACTION = 'SEND_TO_AGENDA_EXTRACTION',
  COMPLETE_AGENDA_EXTRACTION = 'COMPLETE_AGENDA_EXTRACTION',

  CREATE_USER = 'CREATE_USER',
  DELETE_USER = 'DELETE_USER',
  CREATE_ORGANIZATION = 'CREATE_ORGANIZATION',
  UPDATE_ORGANIZATION_SETTINGS = 'UPDATE_ORGANIZATION_SETTINGS',

  EDITOR_POLLING_FALLBACK = 'EDITOR_POLLING_FALLBACK',
}
export type MinutesStyling = {
  minutesVerboseness: MinutesVerboseness | null
  minutesAnonymization: MinutesAnonymization | null
  minutesPassive: MinutesPassive | null
  minutesNameFormat: MinutesNameFormat | null
  minutesDateFormat: MinutesDateFormat | null
  minutesTimeFormat: MinutesTimeFormat | null
  minutesLanguage: MinutesLanguage | null
  minutesAttribution: MinutesAttribution | null
}

export type UploadCallData = {
  name: string
  date: Date
  userIds: number[]
}

export type AddCallData = {
  link: string
  name: string
  userIds: number[]
  date: Date
  duration: number
}

export type ImportResourceData = {
  uploadKey: string
  targetResourceExtension: string
  fileName: string
  sourceDocumentFormat: 'PDF' | 'DOCX' | 'PPTX'
}

type AdminCallQueryResult = {
  list: AdminCallDetails[]
  total: number
}

export enum AgendaItemCategory {
  SUBSTANTIVE = 'SUBSTANTIVE',
  PROCEDURAL = 'PROCEDURAL',
}

export enum AgendaItemSubCategory {
  INTRODUCTION = 'INTRODUCTION',
  CHAIR_OF_THE_MEETING = 'CHAIR_OF_THE_MEETING',
  NOTICE_GIVEN_TO_DIRECTORS = 'NOTICE_GIVEN_TO_DIRECTORS',
  QUORUM = 'QUORUM',
  APOLOGIES_FOR_ABSENCE = 'APOLOGIES_FOR_ABSENCE',
  DECLARATIONS_OF_INTERESTS = 'DECLARATIONS_OF_INTERESTS',
  APPROVAL_OF_PREVIOUS_BOARD_MINUTES = 'APPROVAL_OF_PREVIOUS_BOARD_MINUTES',
  MATTERS_ARISING = 'MATTERS_ARISING',
  ANY_OTHER_BUSINESS = 'ANY_OTHER_BUSINESS',
  DATE_OF_NEXT_MEETING = 'DATE_OF_NEXT_MEETING',
  CLOSE_OF_MEETING = 'CLOSE_OF_MEETING',
  CHAIRS_UPDATE = 'CHAIRS_UPDATE',
  CHAIRS_INTRODUCTION = 'CHAIRS_INTRODUCTION',
  OTHER_PROCEDURAL = 'OTHER_PROCEDURAL',
  VERBAL_UPDATE = 'VERBAL_UPDATE',
  REPORT = 'REPORT',
  DECISIONS_APPROVALS = 'DECISIONS_APPROVALS',
  OTHER_SUBSTANTIVE = 'OTHER_SUBSTANTIVE',
}

export function hasUnlabeledSpeakers(call: Call): boolean {
  if (call.userSpeakerMapping === null) {
    return false
  }
  const sufficientlyLongTranscriptSpeakerSlots = (call.transcript.filter((slot) => slot.type === 'speaker') as TranscriptSpeakerSlot[]).filter(
    (slot) => slot.duration > MINIMUM_SEGMENT_DURATION
  )
  const speakerTagsInTranscript = sufficientlyLongTranscriptSpeakerSlots.map((transcriptSlot) => transcriptSlot.speaker.name!)
  const uniqueTags = [...new Set(speakerTagsInTranscript)]
  return uniqueTags.some((tag) => call.userSpeakerMapping![tag] === tag)
}

export function isDraftCall(call: { type: CallType; status: CallStatus }): boolean {
  return (call.type === CallType.VIDEO_UPLOAD || call.type === CallType.AUDIO_UPLOAD) && call.status === CallStatus.CREATED
}

export function hasEditAccess(call: { accessLevel: string }): boolean {
  return ['FULL_ACCESS', 'EDITOR'].includes(call.accessLevel)
}

/** Edit the minutes as tracked suggestions. A reviewer may do this but may not edit directly. */
export function hasSuggestAccess(call: { accessLevel: string }): boolean {
  return ['FULL_ACCESS', 'EDITOR', 'REVIEWER'].includes(call.accessLevel)
}

export function hasCommentAccess(call: { accessLevel: string }): boolean {
  return ['FULL_ACCESS', 'EDITOR', 'REVIEWER', 'COMMENTER'].includes(call.accessLevel)
}

/** Resolve a thread someone else started. Anyone with comment access may still resolve their own. */
export function canResolveAnyThread(call: { accessLevel: string }): boolean {
  return ['FULL_ACCESS', 'EDITOR', 'REVIEWER'].includes(call.accessLevel)
}

/** Mirrors the backend guard on the editor export endpoints. Every level clears it now that VIEWER is gone, so it stands as the guard's mirror rather than as a live restriction. */
export function canDownloadMinutes(call: { accessLevel: string; allowMinutesDownload: boolean }): boolean {
  return call.allowMinutesDownload && hasCommentAccess(call)
}

/** Whether a regeneration is in flight that rewrites only some agenda items, leaving the rest usable. */
export function isMinutesGenerationTargeted(call: { minutesGenerationTargetTemplateItemIds: number[] | null }): boolean {
  return call.minutesGenerationTargetTemplateItemIds !== null
}

/** Whether the outputs are stale relative to the call, independent of whether reprocessing them is possible right now. */
export function areOutputsStale(call: { isMinutingUpToDate: boolean; isOutlineUpToDate: boolean }): boolean {
  return !call.isMinutingUpToDate || !call.isOutlineUpToDate
}

/**
 * Whether to offer a reprocessing: the outputs are behind the call, and nothing is already redoing them.
 *
 * `isMinutesRegenerationInFlight` rather than `isMinutesGenerationOngoing` — a reprocessing that dispatches an
 * agenda-to-transcript mapping first spends that whole stage with its minutes job not yet started, and offering
 * the button there dispatches a second reprocessing on top of the one running.
 */
export function canReprocessOutputs(call: { isMinutingUpToDate: boolean; isOutlineUpToDate: boolean; isMinutesRegenerationInFlight: boolean }): boolean {
  return areOutputsStale(call) && !call.isMinutesRegenerationInFlight
}

/**
 * Whether the reprocess-outputs button is worth rendering at all: either there's something stale to redo, or a
 * regeneration is already under way and the button should stay visible - disabled, with a tooltip - to explain
 * why, rather than vanishing with no indication of what's happening. Unlike {@link canReprocessOutputs}, this
 * says nothing about whether clicking it would actually work right now.
 */
export function isReprocessOutputsButtonVisible(call: Call): boolean {
  return areOutputsStale(call) || call.isMinutesRegenerationInFlight
}

/**
 * Whether the call has settled enough to stop polling its details.
 *
 * `isPostProcessingComplete` on its own is not enough. It counts minutes generation as not required until
 * that job registers as started, so a call with no agenda flips the flag the moment the outline lands -
 * while the very same response still reports `GENERATING_MINUTES`. Stopping the poll there leaves that
 * progress bar on screen for good, since nothing will ever fetch the state that clears it.
 */
export function hasCallDetailsSettled(call: { isPostProcessingComplete: boolean; processingStatus: CallProcessingStatus | null }): boolean {
  return call.isPostProcessingComplete && (call.processingStatus ?? null) === null
}

type EditableCall = {
  accessLevel: string
  status: CallStatus
  isPostProcessingComplete: boolean
  minutesGenerationTargetTemplateItemIds: number[] | null
}

/** Whether the minutes are in a state that accepts changes at all, ignoring who is asking. */
function areMinutesModifiable(call: EditableCall): boolean {
  // A targeted regeneration leaves post-processing incomplete while it runs, but only the agenda items it covers
  // are off limits — the editor locks those individually and the rest of the minutes stays editable.
  return !(call.status === CallStatus.COMPLETED && !call.isPostProcessingComplete && !isMinutesGenerationTargeted(call))
}

export function canUserEdit(call: EditableCall): boolean {
  return hasEditAccess(call) && areMinutesModifiable(call)
}

/** A reviewer's counterpart to {@link canUserEdit}: the editor accepts input, but only as tracked changes. */
export function canUserSuggest(call: EditableCall): boolean {
  return hasSuggestAccess(call) && areMinutesModifiable(call)
}

export type SupportCreateCallData = {
  name: string
  link: string
  date: Date
  duration: number
  organizationId: number
  userIds: number[]
}

export type LoadTestData = {
  name: string
  botCount: number
  link: string
  date: Date
  duration: number
  organizationId: number
  userIds: number[]
}

type AdminCallListQueryFilters = {
  link?: string
  organizationIds?: number[]
  displaySetToJoinOnly?: boolean
  userIds?: number[]
  statuses?: CallStatus[]
  startDate?: string
  endDate?: string
}
type AdminCallListQuery = AdminCallListQueryFilters & ListParameterQueryOptional

export type AdminCompletedCallUser = {
  userId: number
  firstName: string
  lastName: string
  email: string
}

export type AdminCompletedCall = {
  callId: number
  organization: { id: number; name: string; client: { id: number; name: string } }
  users: AdminCompletedCallUser[]
  status: CallStatus
  type: CallType
  actualStartDate: string | null
  actualEndDate: string | null
  duration: number | null
  hardDeletedAt: string | null
  avgConfidenceScore: number | null
  avgRms: number | null
  avgHfRatio: number | null
}

type AdminCompletedCallQueryResult = {
  list: AdminCompletedCall[]
  total: number
}

type AdminCompletedCallListQueryFilters = {
  callId?: number
  organizationIds?: number[]
  userIds?: number[]
}
type AdminCompletedCallListQuery = AdminCompletedCallListQueryFilters & ListParameterQueryOptional

export type AgendaTranscriptMappingJobStatus = 'NOT_STARTED' | 'ONGOING' | 'SUCCEEDED' | 'FAILED'

export type AgendaTranscriptMappingListItem = {
  callId: number
  organizationId: number
  organizationName: string
  clientName: string
  callDate: string
  durationSeconds: number
  jobRunId: number
  jobStatus: AgendaTranscriptMappingJobStatus
  agendaItemCount: number
  discussedCount: number
  notDiscussedCount: number
  lowConfidenceSegmentCount: number
  unmappedDiscussionCount: number
  transcriptionCoveragePercent: number | null
  isManuallyEdited: boolean
}

type AgendaTranscriptMappingListQueryResult = { list: AgendaTranscriptMappingListItem[]; total: number }

export type AgendaTranscriptMappingListQuery = {
  callId?: number
  organizationIds?: number[]
  startDate?: string
  endDate?: string
  durationMinSeconds?: number
  durationMaxSeconds?: number
  jobStatus?: AgendaTranscriptMappingJobStatus
  hasLowConfidenceSegments?: boolean
  hasUnmappedDiscussions?: boolean
  discussedRatioMax?: number
  coverageMax?: number
  sortField?: 'callId' | 'callDate' | 'durationSeconds'
  sortDirection?: 'asc' | 'desc'
  page?: number
  pageSize?: number
}

export type MergeCallRecordingBlocker =
  | 'SAME_CALL'
  | 'DIFFERENT_ORGANIZATIONS'
  | 'TARGET_HAS_NO_RECORDING'
  | 'SOURCE_HAS_NO_RECORDING'
  | 'SOURCE_FILES_MISSING'
  | 'RECORDING_FORMAT_MISMATCH'
  | 'UPLOADED_RECORDING'
  | 'SEGMENTS_MANIFEST_MISMATCH'
  | 'NATIVE_TEAMS_RECORDING'
  | 'TARGET_IS_ONGOING'
  | 'SOURCE_IS_ONGOING'
  | 'TARGET_MARKED_FOR_DELETION'

export type MergeCallRecordingSummary = {
  id: number
  organizationId: number
  organizationName: string
  clientName: string
  date: string
  status: CallStatus
  type: CallType
  resourceType: string | null
  extension: string | null
  recordingSessionCount: number
  fileCount: number
}

export type MergeCallRecordingPreview = {
  target: MergeCallRecordingSummary
  source: MergeCallRecordingSummary
  blockers: MergeCallRecordingBlocker[]
  sessionsToAppend: number
  filesToCopy: number
}

export type MergeCallRecordingResult = {
  jobId: number
  appendedSessionIndices: number[]
  copiedFileCount: number
}

type CallDetailsErrorUser = {
  id: number
  firstName: string
  lastName: string
  email: string
}
type CallDetailsError = {
  hasAccess: boolean
  sameOrganization: boolean
  callName: string
  users: CallDetailsErrorUser[]
}
type CallDetailsErrorData = {
  error: { message: CallDetailsError }
}

@Injectable({
  providedIn: 'root',
})
export class CallsService {
  mutation = injectMutation()
  httpClient = inject(HttpClient)
  query = injectQuery()
  infiniteQuery = injectInfiniteQuery()
  queryClient = injectQueryClient()
  toastService = inject(ToastService)
  downloadsService = inject(DownloadsService)
  demoProfilesService = inject(DemoProfilesService)
  loggerService = inject(LoggerService)
  metricsService = inject(MetricsService)

  private ongoingCallsFastPollEndsAt: Date | null = null

  searchGuestOrganizationOptionsQueryParams = {
    queryKey: queryKeys.search.guestOrganizations,
    queryFn: () => this.httpClient.get<GuestOrganization[]>('/api/search/options/guest-organizations'),
  }

  searchAttendeeOptionsQueryParams = {
    queryKey: queryKeys.search.attendees,
    queryFn: () => this.httpClient.get<Attendee[]>('/api/search/options/attendees'),
  }

  searchSpeakerOptionsQueryParam = {
    queryKey: queryKeys.search.speakers,
    queryFn: () => this.httpClient.get<Speaker[]>('/api/search/options/speakers'),
  }

  constructor() {
    this.listenForActiveDemoProfileUpdates()
  }

  forceProcessBotOutputsMutation() {
    return this.mutation({
      mutationFn: (callId: number) => this.httpClient.post<void>(`api/calls/${callId}/process-bot-outputs`, null),
      onSuccess: (_, callId) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(callId) })
        this.toastService.show('CALLS.ADMIN.FORCE_PROCESS_BOT_OUTPUTS_SUCCESS')
      },
      onError: () => {
        this.toastService.show('CALLS.ADMIN.FORCE_PROCESS_BOT_OUTPUTS_FAILURE')
      },
    })
  }

  /**
   * Checks a pair of meetings before offering the merge: the API answers with both meetings and the
   * reasons, if any, their recordings cannot be combined.
   */
  previewRecordingMerge(targetCallId: number, sourceCallId: number): Observable<MergeCallRecordingPreview> {
    return this.httpClient.get<MergeCallRecordingPreview>(`/api/calls/${targetCallId}/merge-recording-preview`, { params: { sourceCallId } })
  }

  mergeRecordingMutation() {
    return this.mutation({
      mutationFn: ({ targetCallId, sourceCallId }: { targetCallId: number; sourceCallId: number }) =>
        this.httpClient.post<MergeCallRecordingResult>(`/api/calls/${targetCallId}/merge-recording`, { sourceCallId }),
      onSuccess: (_, { targetCallId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(targetCallId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(targetCallId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.base })
        this.toastService.show('CALLS.ADMIN.MERGE_RECORDING.SUCCESS')
      },
      onError: () => {
        this.toastService.show('CALLS.ADMIN.MERGE_RECORDING.FAILURE')
      },
    })
  }

  splitCallMutation() {
    return this.mutation({
      mutationFn: ({ callId, recordingPositions }: { callId: number; recordingPositions: number[] }) =>
        this.httpClient.post<void>(`/api/calls/${callId}/split`, { recordingPositions }),
      onSuccess: (_, { callId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(callId) })
        this.toastService.show('CALLS.ADMIN.SPLIT_CALL.SUCCESS')
      },
      onError: () => {
        this.toastService.show('CALLS.ADMIN.SPLIT_CALL.FAILURE')
      },
    })
  }

  reprocessOutputsMutation() {
    return this.mutation({
      mutationFn: ({ callId }: { callId: number }) => this.httpClient.post<void>(`api/calls/${callId}/reprocess-outputs`, {}),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
        this.toastService.show('CALLS.REPROCESS.SUCCESS')
      },
      onError: () => {
        this.toastService.show('CALLS.REPROCESS.FAILURE')
      },
    })
  }

  createEditorDocumentMutation() {
    return this.mutation({
      mutationFn: ({ callId }: { callId: number }) => this.httpClient.post<void>(`api/calls/${callId}/editor-document`, {}),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
      },
    })
  }

  reprocessTemplateItemsMutation() {
    return this.mutation({
      mutationFn: ({ callId, templateItemIds }: { callId: number; templateItemIds: number[] }) =>
        this.httpClient.post<void>(`api/calls/${callId}/template-items/reprocess`, { templateItemIds }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.byCallId(params.callId) })
        this.toastService.show('CALLS.REPROCESS.SUCCESS')
      },
      onError: () => {
        this.toastService.show('CALLS.REPROCESS.FAILURE')
      },
    })
  }

  sendRecoveryBotMutation() {
    return this.mutation({
      mutationFn: (callId: number) => this.httpClient.post<SendRecoveryBotResult>(`api/calls/${callId}/send-recovery-bot`, null),
      onSuccess: (result, callId) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.client.base })
        this.toastService.show(SEND_RECOVERY_BOT_TOASTS[result.outcome])
      },
      onError: () => {
        this.toastService.show('CALLS.ADMIN.SEND_RECOVERY_BOT.FAILURE')
      },
    })
  }

  sendDirectBotMutation() {
    return this.mutation({
      mutationFn: ({ callId, startNow }: { callId: number; startNow: boolean }) =>
        this.httpClient.post<void>(`api/calls/${callId}/send-direct-bot`, { startNow }),
      onSuccess: (_, { callId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(callId) })
        this.toastService.show('CALLS.ADMIN.SEND_DIRECT_BOT.SUCCESS')
      },
      onError: () => {
        this.toastService.show('CALLS.ADMIN.SEND_DIRECT_BOT.FAILURE')
      },
    })
  }

  resendBotMutation() {
    return this.mutation({
      mutationFn: (callId: number) => this.httpClient.post<ResendBotResult>(`api/calls/${callId}/resend-bot`, null),
      onSuccess: (result, callId) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
        this.toastService.show(result.outcome === 'ALREADY_ON_ITS_WAY' ? 'CALLS.RESEND_BOT.ALREADY_ON_ITS_WAY' : 'CALLS.RESEND_BOT.SUCCESS')
      },
      onError: () => {
        this.toastService.show('CALLS.RESEND_BOT.FAILURE')
      },
    })
  }

  switchToBrowserBotMutation() {
    return this.mutation({
      mutationFn: (callId: number) =>
        this.httpClient.post<{ outcome: 'SWITCHED' | 'ALREADY_JOINED' | 'ALREADY_ON_ITS_WAY' }>(`api/calls/${callId}/switch-to-browser-bot`, null),
      onSuccess: (result, callId) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
        this.toastService.show(`CALLS.SWITCH_TO_BROWSER_BOT.${result.outcome}`)
      },
      onError: () => {
        this.toastService.show('CALLS.SWITCH_TO_BROWSER_BOT.FAILURE')
      },
    })
  }

  reprocessComponentMutation() {
    return this.mutation({
      mutationFn: ({
        callId,
        component,
        llmModel,
        llmEffort,
        stopAfterCompletion,
        minSpeakers,
        speakerCount,
        maxSpeakers,
        isDry,
        bypassNewAgendaTranscriptMappingJob,
        skipSilenceTrimming,
      }: {
        callId: number
        component: PostProcessor
        llmModel: LlmModel | null
        llmEffort: LlmEffort | null
        stopAfterCompletion: boolean
        minSpeakers: number | null
        speakerCount: number | null
        maxSpeakers: number | null
        isDry: boolean
        bypassNewAgendaTranscriptMappingJob: boolean
        skipSilenceTrimming: boolean
      }) => {
        const params = new URLSearchParams({ component })
        if (llmModel !== null) {
          params.append('llmModel', llmModel)
        }
        if (llmEffort !== null) {
          params.append('llmEffort', llmEffort)
        }
        if (stopAfterCompletion) {
          params.append('stopAfterCompletion', 'true')
        }
        if (minSpeakers !== null) {
          params.append('minSpeakers', String(minSpeakers))
        }
        if (speakerCount !== null) {
          params.append('speakerCount', String(speakerCount))
        }
        if (maxSpeakers !== null) {
          params.append('maxSpeakers', String(maxSpeakers))
        }
        if (isDry) {
          params.append('isDry', 'true')
        }
        if (bypassNewAgendaTranscriptMappingJob) {
          params.append('bypassNewAgendaTranscriptMappingJob', 'true')
        }
        if (skipSilenceTrimming) {
          params.append('skipSilenceTrimming', 'true')
        }
        return this.httpClient.post<void>(`api/calls/${callId}/reprocess?${params.toString()}`, null)
      },
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
        this.toastService.show('CALLS.REPROCESS.SUCCESS')
      },
      onError: () => {
        this.toastService.show('CALLS.REPROCESS.FAILURE')
      },
    })
  }

  getUpcomingCalls() {
    return this.infiniteQuery({
      queryKey: queryKeys.calls.list.upcoming,
      queryFn: ({ pageParam }) => this.getUpcomingCallList(pageParam),
      initialPageParam: 0,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      refetchInterval: 10000,
    })
  }

  getOngoingCalls() {
    return this.query({
      queryKey: queryKeys.calls.list.ongoing,
      queryFn: () => this.getOngoingCallList(),
      refetchInterval: () => (this.ongoingCallsFastPollEndsAt !== null && new Date() < this.ongoingCallsFastPollEndsAt ? 1_000 : 10_000),
    }).result$
  }

  private pollOngoingCallsFast(): void {
    this.ongoingCallsFastPollEndsAt = new Date(Date.now() + ONGOING_CALLS_FAST_POLL_DURATION_MS)
    this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.list.ongoing })
  }

  getPastCalls(callType: PastCallsType, showHiddenCalls: boolean) {
    return this.infiniteQuery({
      queryKey: queryKeys.calls.list.past.byType(callType),
      queryFn: ({ pageParam }) => this.getPastCallList(pageParam, callType, showHiddenCalls),
      initialPageParam: 0,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      refetchInterval: 10000,
    })
  }
  getAdminCalls(params: AdminCallListQuery) {
    return this.query({
      queryKey: queryKeys.calls.base,
      queryFn: () => this.httpClient.get<AdminCallQueryResult>('/api/calls/support', { params }),
    }).result$
  }

  getSupportCompletedCalls(params: AdminCompletedCallListQuery) {
    return this.query({
      queryKey: [...queryKeys.calls.support.completed, params],
      queryFn: () => this.httpClient.get<AdminCompletedCallQueryResult>('/api/calls/support/completed-calls', { params }),
    }).result$
  }

  getSupportAgendaTranscriptMappingList(params: AgendaTranscriptMappingListQuery) {
    return this.query({
      queryKey: [...queryKeys.calls.support.agendaTranscriptMappings, params],
      queryFn: () => this.httpClient.get<AgendaTranscriptMappingListQueryResult>('/api/calls/support/agenda-transcript-mappings', { params }),
    }).result$
  }

  getAgendaTranscriptMappingForCall(callId: number, jobId: number) {
    return this.query({
      queryKey: queryKeys.calls.support.agendaTranscriptMapping(callId, jobId),
      queryFn: () =>
        this.httpClient.get<AgendaTranscriptMappingResponse | null>(`/api/calls/${callId}/support/agenda-transcript-mapping`, { params: { jobId } }),
    }).result$
  }

  updateAgendaTranscriptMapping() {
    return this.mutation({
      mutationFn: ({ callId, jobId, items }: { callId: number; jobId: number; items: UpdateAgendaTranscriptMappingItem[] }) =>
        this.httpClient.put<void>(`/api/calls/${callId}/support/agenda-transcript-mapping`, { items }, { params: { jobId } }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.agendaTranscriptMapping(params.callId, params.jobId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.agendaTranscriptMappings })
      },
      onError: () => {
        this.toastService.show('CALLS.ADMIN.A2T_EDIT.SAVE_FAILED')
      },
    })
  }

  getAdminCallsForUser(userId: number, params: AdminCallListQuery) {
    return this.query({
      queryKey: [...queryKeys.calls.support.byUserId(userId), params],
      queryFn: () => this.httpClient.get<AdminCallQueryResult>('/api/calls/support', { params: { ...params, userIds: [userId] } }),
    }).result$
  }

  getAdminCallsForOrganization(organizationId: number, params: AdminCallListQuery) {
    return this.query({
      queryKey: [...queryKeys.calls.support.byOrganizationId(organizationId), params],
      queryFn: () => this.httpClient.get<AdminCallQueryResult>('/api/calls/support', { params: { ...params, organizationIds: [organizationId] } }),
    }).result$
  }

  getCall(id: number) {
    return this.query<Call, CallDetailsErrorData>(this.getCallQueryParams(id)).result$
  }

  getAdminViewToCall(id: number): Observable<QueryObserverResult<AdminCallDetails>> {
    return this.query({
      queryKey: queryKeys.calls.support.byId(id),
      queryFn: () => this.httpClient.get<AdminCallDetails>(`/api/calls/${id}/support`),
      refetchInterval: 30000,
    }).result$
  }

  getDebugDataFiles(id: number): Observable<QueryObserverResult<DebugDataFile[]>> {
    return this.query({
      queryKey: queryKeys.calls.support.debugData(id),
      queryFn: () => this.httpClient.get<DebugDataFile[]>(`/api/calls/${id}/support/debug-data`),
    }).result$
  }

  getEditorDebug(id: number): Observable<QueryObserverResult<EditorAdminDebug | null>> {
    return this.query({
      queryKey: queryKeys.calls.support.editorDebug(id),
      queryFn: () => this.httpClient.get<EditorAdminDebug | null>(`/api/calls/${id}/editor/support-debug`),
    }).result$
  }

  getHallucinationAudit(callId: number, jobId: number): Observable<QueryObserverResult<HallucinationAuditData>> {
    return this.query({
      queryKey: queryKeys.calls.support.hallucinationAudit(callId, jobId),
      queryFn: () => this.httpClient.get<HallucinationAuditData>(`/api/calls/${callId}/support/hallucination-audit/${jobId}`),
    }).result$
  }

  private getCallQueryParams(id: number) {
    let failureCount = 0

    return {
      queryKey: queryKeys.calls.byId(id),
      queryFn: () => this.httpClient.get<Call>(`/api/calls/${id}`),
      retry: 0,
      refetchInterval: (query: Query<Call, CallDetailsErrorData>) => {
        if (query.state.data?.isIngestingBoardPack ?? false) {
          return 10000
        }

        const call = query.state.data ?? null
        if (call !== null && hasCallDetailsSettled(call)) {
          return false
        }

        if (query.state.error?.error?.message?.sameOrganization) {
          return false
        }

        const failed = query.state.fetchFailureReason !== null
        if (failed) {
          failureCount++
          if (failureCount === 3) {
            this.loggerService.error('The call details fetching failed three times. Stopping the refetch.')
            return false
          }
        }

        if (query.state.data?.isCheckingAgendaValidity ?? false) {
          return 1000
        }

        return 10000
      },
      staleTime: 10000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    }
  }

  async prefetchCall(id: number) {
    await this.queryClient.prefetchQuery(this.getCallQueryParams(id))
  }

  getCallsOfUser(): Observable<QueryObserverResult<Call[]>> {
    const upcomingCalls$ = this.httpClient
      .get<CallListResponse>('/api/calls/upcoming', {
        params: { cursor: 0, limit: 1000 },
      })
      .pipe(map((response) => response.calls))
    const pastCalls$ = this.httpClient
      .get<CallListResponse>('/api/calls/past', {
        params: {
          cursor: 0,
          limit: 1000,
          callType: 'ALL',
          showHiddenCalls: true,
        },
      })
      .pipe(map((response) => response.calls))
    return forkJoin([upcomingCalls$, pastCalls$]).pipe(
      map(([upcomingCalls, pastCalls]) => [...upcomingCalls, ...pastCalls]),
      mergeMap(
        (calls) =>
          this.query<Call[]>({
            queryKey: queryKeys.calls.base,
            queryFn: () => of(calls),
          }).result$
      )
    )
  }

  uploadCall() {
    return this.mutation({
      mutationFn: (data: UploadCallData) => this.httpClient.post<Call>(`/api/calls/upload`, data),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.list.past.base })
      },
    })
  }

  joinLiveCall() {
    return this.mutation({
      mutationFn: (data: AddCallData) => this.httpClient.post<Call>(`/api/calls`, data),
      onError: (error: any) => {
        const errorMessages: { [key: string]: string } = {
          CALL_WITH_LINK_ALREADY_EXISTS: 'CALLS.ADD_CALL.CALL_WITH_LINK_ALREADY_EXISTS',
          CALL_LINK_INVALID: 'CALLS.ADD_CALL.CALL_LINK_INVALID',
          TEAMS_SHORT_LINK_NOT_SUPPORTED: 'CALLS.ADD_CALL.TEAMS_SHORT_LINK_NOT_SUPPORTED',
        }
        const errorMessage = errorMessages[error?.error?.message] || 'CALLS.ADD_CALL.FAILURE'
        this.toastService.show(errorMessage)
      },
    })
  }

  importResource() {
    return this.mutation({
      mutationFn: ({ callId, data }: { callId: number; data: ImportResourceData }) =>
        this.httpClient.post<CallResource>(`/api/calls/${callId}/import-resource`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.toastService.show('CALLS.CALL_INFO.DOCUMENT_UPLOAD.IMPORT_SUCCESS')
      },
    })
  }

  importAgenda() {
    return this.mutation({
      mutationFn: ({ callId, data }: { callId: number; data: ImportResourceData }) =>
        this.httpClient.post<CallResource>(`/api/calls/${callId}/import-agenda`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.toastService.show('CALLS.CALL_INFO.DOCUMENT_UPLOAD.IMPORT_SUCCESS')
      },
    })
  }

  removeResource() {
    return this.mutation({
      mutationFn: ({ callId, resourceType, index, extension }: { callId: number; resourceType: string; index: number; extension: string }) =>
        this.httpClient.delete<void>(`/api/calls/${callId}/resources`, {
          body: { resourceType, index, extension },
        }),
      // Awaiting the refetch keeps the mutation pending until the removed resource has left the page, so its remove button cannot fire a second delete.
      onSuccess: (_, params) =>
        Promise.all([
          this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) }),
          this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.byCallId(params.callId) }),
        ]),
      onError: () => {
        this.toastService.show('CALLS.CALL_INFO.DOCUMENT_UPLOAD.REMOVE_FAILED')
      },
    })
  }

  removeAllResourcesOfType() {
    return this.mutation({
      mutationFn: ({ callId, resourceType }: { callId: number; resourceType: string }) =>
        this.httpClient.delete<void>(`/api/calls/${callId}/resources/by-type`, {
          body: { resourceType },
        }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.byCallId(params.callId) })
      },
      onError: () => {
        this.toastService.show('CALLS.CALL_INFO.DOCUMENT_UPLOAD.REMOVE_FAILED')
      },
    })
  }

  removeResourceSupportMutation() {
    return this.mutation({
      mutationFn: ({ callId, resourceType, index, extension }: { callId: number; resourceType: string; index: number; extension: string }) =>
        this.httpClient.delete<void>(`/api/calls/${callId}/resources/support`, {
          body: { resourceType, index, extension },
        }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
      },
      onError: () => {
        this.toastService.show('CALLS.CALL_INFO.DOCUMENT_UPLOAD.REMOVE_FAILED')
      },
    })
  }

  importRecording() {
    return this.mutation({
      mutationFn: ({ callId, uploadKey, fileExtension, fileName }: { callId: number; uploadKey: string; fileExtension: string; fileName: string }) =>
        this.httpClient.post<Call>(`/api/calls/${callId}/import-recording`, { uploadKey, fileExtension, fileName }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
      },
      onError: () => {
        this.toastService.show('CALL_SETUP.UPLOAD_RECORDING.UPLOAD_FAILED')
      },
    })
  }

  completeSetup() {
    return this.mutation({
      mutationFn: ({ callId }: { callId: number }) => this.httpClient.post<Call>(`/api/calls/${callId}/complete-setup-for-uploaded-call`, {}),
      onSuccess: (_, params) => {
        this.queryClient.resetQueries({ queryKey: queryKeys.calls.byId(params.callId) })
      },
      onError: () => {
        this.toastService.show('CALL_SETUP.COMPLETE_SETUP.FAILED')
      },
    })
  }

  patchMutation() {
    return this.mutation({
      mutationFn: ({ callId, data }: { callId: number; data: Partial<Call> }) => this.httpClient.patch<void>(`/api/calls/${callId}`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.byCallId(params.callId) })
      },
    })
  }

  patchSupportMutation() {
    return this.mutation({
      mutationFn: ({
        callId,
        data,
      }: {
        callId: number
        data: Omit<
          Partial<Call>,
          | 'useNativeBot'
          | 'minutingAgents'
          | 'overrideOrganizationLevelMinutingAgents'
          | 'useVerificationDiscovery'
          | 'anonymiseMemberSpeakers'
          | 'useTranscriptRefinement'
          | 'allowRestrictedUsers'
        > & {
          useNativeBot?: boolean | null
          minutingAgents?: MinutingAgent[]
          overrideOrganizationLevelMinutingAgents?: boolean
          useVerificationDiscovery?: boolean | null
          useAgendaTranscriptMapping?: AgendaTranscriptMappingMode | null
          anonymiseMemberSpeakers?: boolean | null
          useTranscriptRefinement?: boolean | null
          allowRestrictedUsers?: boolean | null
        }
      }) => this.httpClient.patch<void>(`/api/calls/${callId}/support`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.byCallId(params.callId) })
      },
    })
  }

  updateAgendaTypeMutation() {
    return this.mutation({
      mutationFn: ({ callId, data }: { callId: number; data: { agendaType: AgendaType } }) =>
        this.httpClient.post<void>(`/api/calls/${callId}/agenda-type`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
      },
    })
  }

  updateAgendaTypeSupportMutation() {
    return this.mutation({
      mutationFn: ({ callId, data }: { callId: number; data: { agendaType: AgendaType } }) =>
        this.httpClient.post<void>(`/api/calls/${callId}/agenda-type/set-as-support`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
      },
    })
  }

  updateMediumMutation() {
    return this.mutation({
      mutationFn: ({ callId, data }: { callId: number; data: { medium: CallMedium } }) => this.httpClient.post<void>(`/api/calls/${callId}/medium`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
      },
    })
  }

  updateMediumSupportMutation() {
    return this.mutation({
      mutationFn: ({ callId, data }: { callId: number; data: { medium: CallMedium } }) =>
        this.httpClient.post<void>(`/api/calls/${callId}/medium/set-as-support`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
      },
    })
  }

  updateCallProfileMutation() {
    return this.mutation({
      mutationFn: ({ callId, data }: { callId: number; data: { callProfileId: number | null } }) =>
        this.httpClient.post<void>(`/api/calls/${callId}/call-profile`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
      },
    })
  }

  updateCallProfileSupportMutation() {
    return this.mutation({
      mutationFn: ({ callId, data }: { callId: number; data: { callProfileId: number | null } }) =>
        this.httpClient.post<void>(`/api/calls/${callId}/call-profile/set-as-support`, data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
      },
    })
  }

  assignTemplateSupportMutation() {
    return this.mutation({
      mutationFn: ({ callId, templateId }: { callId: number; templateId: number }) =>
        this.httpClient.post<void>(`/api/calls/${callId}/assign-template`, { templateId }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.byCallId(params.callId) })
        this.toastService.show('TEMPLATES.IMPORTED')
      },
      onError: () => {
        this.toastService.show('TEMPLATES.IMPORT_FAILED')
      },
    })
  }

  splitSpeakersMutation() {
    return this.mutation({
      mutationFn: ({ callId, speakerTag }: { callId: number; speakerTag: string }) =>
        this.httpClient.post<void>(`/api/calls/${callId}/split-speakers`, { speakerTag }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.toastService.show('CALLS.SPEAKERS.SPLIT_SUCCEEDED')
      },
      onError: () => {
        this.toastService.show('CALLS.SPEAKERS.SPLIT_FAILED')
      },
    })
  }

  patchSpeakerNameMutation() {
    return this.mutation({
      mutationFn: ({ callId, speakerTag, speakerName }: { callId: number; speakerTag: string; speakerName: string }) =>
        this.httpClient.patch<void>(`/api/calls/${callId}/speaker-mapping`, { speakerTag, speakerName }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
      },
    })
  }

  pauseRecording(callId: number) {
    return this.httpClient.post<void>(`/api/calls/${callId}/pause-recording`, {}).subscribe({
      next: () => {
        this.pollOngoingCallsFast()
        this.toastService.show('CALLS.ONGOING_CALLS.RECORDING_PAUSE.SUCCESS')
      },
      error: () => {
        this.toastService.show('CALLS.ONGOING_CALLS.RECORDING_PAUSE.FAILURE')
      },
    })
  }

  resumeRecording(callId: number) {
    return this.httpClient.post<void>(`/api/calls/${callId}/resume-recording`, {}).subscribe({
      next: () => {
        this.pollOngoingCallsFast()
        this.toastService.show('CALLS.ONGOING_CALLS.RECORDING_RESUME.SUCCESS')
      },
      error: () => {
        this.toastService.show('CALLS.ONGOING_CALLS.RECORDING_RESUME.FAILURE')
      },
    })
  }

  hangUpCall(callId: number) {
    this.httpClient.post<void>(`/api/calls/${callId}/hang-up`, {}).subscribe({
      next: () => {
        this.toastService.show('CALLS.HANG_UP.SUCCESS')
      },
      error: () => {
        this.toastService.show('CALLS.HANG_UP.FAILURE')
      },
    })
  }

  hangUpAsSupportMutation() {
    return this.mutation({
      mutationFn: (callId: number) => this.httpClient.post<void>(`/api/calls/${callId}/hang-up-as-support`, {}),
      onSuccess: (_, callId) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(callId) })
        this.toastService.show('CALLS.HANG_UP.SUCCESS')
      },
      onError: () => {
        this.toastService.show('CALLS.HANG_UP.FAILURE')
      },
    })
  }

  solveCaptchaMutation() {
    return this.mutation({
      mutationFn: ({ callId, code }: { callId: number; code: string }) => this.httpClient.post<void>(`/api/calls/${callId}/support/solve-captcha`, { code }),
      onSuccess: (_, { callId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.debugData(callId) })
        this.toastService.show('CALLS.ADMIN.SOLVE_CAPTCHA.SUCCESS')
      },
      onError: () => {
        this.toastService.show('CALLS.ADMIN.SOLVE_CAPTCHA.FAILURE')
      },
    })
  }

  hangUpCallAnonymously(callId: number, accessKey: string): Observable<boolean> {
    return this.httpClient.post<void>(`/api/bot/${callId}/hang-up`, {}, { headers: { 'X-Call-Access-Key': accessKey } }).pipe(
      map(() => {
        this.toastService.show('CALLS.HANG_UP.SUCCESS')
        return true
      }),
      catchError(() => {
        this.toastService.show('CALLS.HANG_UP.FAILURE')
        return of(false)
      })
    )
  }

  previewExtendCallAnonymously(callId: number, accessKey: string): Observable<ExtendPreview> {
    return this.httpClient
      .get<ExtendPreview>(`/api/bot/${callId}/extend`, { headers: { 'X-Call-Access-Key': accessKey } })
      .pipe(catchError(() => of({ status: 'ERROR' as const, leaveDate: null })))
  }

  extendCallAnonymously(callId: number, accessKey: string): Observable<ExtendResult> {
    return this.httpClient
      .post<ExtendResult>(`/api/bot/${callId}/extend`, {}, { headers: { 'X-Call-Access-Key': accessKey } })
      .pipe(catchError(() => of({ status: 'ERROR' as const, leaveDate: null })))
  }

  switchToNextSubCallMutation() {
    return this.mutation({
      mutationFn: (callId: number) => this.httpClient.post<void>(`/api/calls/${callId}/save-split-offset`, {}),
      onSuccess: (_, callId) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.list.ongoing })
        this.toastService.show('CALLS.ONGOING_CALLS.PROCEED_TO_NEXT_SUB_CALL.SUCCESS')
      },
      onError: () => {
        this.toastService.show('CALLS.ONGOING_CALLS.PROCEED_TO_NEXT_SUB_CALL.FAILURE')
      },
    })
  }

  setEndDateMutation() {
    return this.mutation({
      mutationFn: ({ callId, endDate }: { callId: number; endDate: Date }) =>
        this.httpClient.post<void>(`/api/calls/${callId}/set-end-date`, { endDate: endDate.toISOString() }),
      onSuccess: (_, { callId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(callId) })
      },
    })
  }

  extendCallMutation() {
    return this.mutation({
      mutationFn: (callId: number) =>
        firstValueFrom(
          this.httpClient.post<ExtendResult>(`/api/calls/${callId}/extend`, {}).pipe(catchError(() => of({ status: 'ERROR' as const, leaveDate: null })))
        ),
      onSuccess: (_, callId) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.list.ongoing })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
      },
    })
  }

  setDeletionDatesMutation() {
    return this.mutation({
      mutationFn: ({ callId, softDeletionDate, hardDeletionDate }: { callId: number; softDeletionDate: Date; hardDeletionDate: Date }) =>
        this.httpClient.post<void>(`/api/calls/${callId}/set-deletion-dates`, {
          softDeletionDate: softDeletionDate.toISOString(),
          hardDeletionDate: hardDeletionDate.toISOString(),
        }),
      onSuccess: (_, { callId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(callId) })
        this.toastService.show('CALLS.ADJUST_RETENTION.SUCCESS')
      },
      onError: () => {
        this.toastService.show('CALLS.ADJUST_RETENTION.FAILURE')
      },
    })
  }

  requestCallAccess(callId: number) {
    return this.httpClient.post<void>(`/api/calls/${callId}/request-access`, {}).subscribe({
      next: () => {
        this.toastService.show('CALLS.ACCESS_REQUEST.SUCCESS')
      },
      error: () => {
        this.toastService.show('CALLS.ACCESS_REQUEST.FAILURE')
      },
    })
  }

  setIsSetToJoinMutation() {
    return this.mutation({
      mutationFn: (callId: number) => this.httpClient.post<void>(`/api/calls/${callId}/set-to-join`, {}),
      onSuccess: (_, callId) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(callId) })
      },
    })
  }

  cancelSessionCloudRunExecutionMutation() {
    return this.mutation({
      mutationFn: ({ callId, sessionIndex }: { callId: number; sessionIndex: number }) =>
        this.httpClient.post<void>(`/api/calls/${callId}/sessions/${sessionIndex}/cancel-cloud-run-execution`, {}),
      onSuccess: (_, { callId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(callId) })
        this.toastService.show('CALLS.ADMIN.CANCEL_CLOUD_RUN_EXECUTION.SUCCESS')
      },
      onError: () => {
        this.toastService.show('CALLS.ADMIN.CANCEL_CLOUD_RUN_EXECUTION.FAILURE')
      },
    })
  }

  setSoftDeletionMutation() {
    return this.mutation({
      mutationFn: ({ callId, markedToBeDeleted }: { callId: number; markedToBeDeleted: boolean }) =>
        this.httpClient.post<void>(`/api/calls/${callId}/set-soft-deletion`, { markedToBeDeleted }),
      onSuccess: (_, { callId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.list.base })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(callId) })
      },
    })
  }

  setCallStatusMutation() {
    return this.mutation({
      mutationFn: ({ callId, status }: { callId: number; status: CallStatus }) => this.httpClient.post<void>(`/api/calls/${callId}/set-status`, { status }),
      onSuccess: (_, { callId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(callId) })
      },
    })
  }

  setCallSessionStatusMutation() {
    return this.mutation({
      mutationFn: ({ callId, sessionIndex, status }: { callId: number; sessionIndex: number; status: CallSessionStatus }) =>
        this.httpClient.post<void>(`/api/calls/${callId}/sessions/${sessionIndex}/set-status`, { status }),
      onSuccess: (_, { callId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(callId) })
      },
    })
  }

  approveAccessRequest() {
    return this.mutation({
      mutationFn: (callId: number) => this.httpClient.post<void>(`/api/calls/${callId}/approve-access`, {}),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.accessRequests.base })
      },
    })
  }

  revokeAccessRequest() {
    return this.mutation({
      mutationFn: (callId: number) => this.httpClient.post<void>(`/api/calls/${callId}/revoke-access`, {}),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.accessRequests.base })
      },
    })
  }

  getCallAccessRequest(callId: number) {
    return this.query({
      queryKey: queryKeys.calls.accessRequests.byId(callId),
      queryFn: () =>
        this.httpClient.get<{
          callId: number
          accessProvided: boolean
        }>(`/api/calls/${callId}/check-access`),
    }).result$
  }

  getCallAccessRequests() {
    return this.query({
      queryKey: queryKeys.calls.accessRequests.base,
      queryFn: () => this.httpClient.get<CallAccessRequest[]>('/api/calls/access-requests'),
    }).result$
  }

  exportTranscript(call: Call, format: 'pdf' | 'docx'): Observable<void> {
    const fileName = generateFileName(call, 'transcript', format)
    return this.httpClient
      .get(`/api/calls/${call.id}/export/${format}?outlineSelected=false&transcriptSelected=true`, {
        responseType: 'blob',
        headers: generateHeadersWithTimezone(),
      })
      .pipe(
        tap((data) => {
          this.downloadsService.downloadData(data, fileName, format)
          this.toastService.show('CALLS.TRANSCRIPT_EXPORT_COMPLETED')
        }),
        map(() => void 0)
      )
  }

  exportMinutes(
    call: Call,
    format: 'pdf' | 'docx',
    entity: { id: number; name: string } | null = null,
    revision: { id: number; createdAt: string } | null = null
  ): Observable<void> {
    const baseFileName = generateFileName(call, 'minutes', format)
    const entityFileName = entity === null ? baseFileName : qualifyFileName(baseFileName, entity.name)
    // Windows rejects a colon in a file name, so the time is written with a dot.
    const fileName =
      revision === null ? entityFileName : qualifyFileName(entityFileName, `version ${formatDate(new Date(revision.createdAt), 'dd-MM-yyyy HH.mm')}`)
    return this.httpClient
      .get(`/api/calls/${call.id}/export-editor-to-${format}`, {
        responseType: 'blob',
        headers: generateHeadersWithTimezone(),
        params: {
          ...(entity === null ? {} : { callEntityId: entity.id }),
          ...(revision === null ? {} : { revisionId: revision.id }),
        },
      })
      .pipe(
        tap((data) => {
          this.downloadsService.downloadData(data, fileName, format)
          this.toastService.show('CALLS.MINUTES_EXPORT_COMPLETED')
        }),
        map(() => void 0)
      )
  }

  exportOutline(call: Call, format: 'pdf' | 'docx'): Observable<void> {
    const fileName = generateFileName(call, 'outline', format)
    return this.httpClient
      .get(`/api/calls/${call.id}/export/${format}?outlineSelected=true&transcriptSelected=false`, {
        responseType: 'blob',
        headers: generateHeadersWithTimezone(),
      })
      .pipe(
        tap((data) => {
          this.downloadsService.downloadData(data, fileName, format)
          this.toastService.show('CALLS.OUTLINE_EXPORT_COMPLETED')
        }),
        map(() => void 0)
      )
  }

  exportActionItems(call: Call, format: 'csv'): Observable<void> {
    const fileName = generateFileName(call, 'action-items', format)
    return this.httpClient
      .get(`/api/calls/${call.id}/export/action-items`, {
        responseType: 'blob',
        headers: generateHeadersWithTimezone(),
      })
      .pipe(
        tap((data) => {
          this.downloadsService.downloadData(data, fileName, format)
          this.toastService.show('CALLS.ACTION_ITEMS_EXPORT_COMPLETED')
        }),
        map(() => void 0)
      )
  }

  getSearchGuestOrganizationOptions() {
    return this.query(this.searchGuestOrganizationOptionsQueryParams).result$
  }

  async prefetchSearchFilterOptions() {
    await this.queryClient.prefetchQuery(this.searchGuestOrganizationOptionsQueryParams)
    await this.queryClient.prefetchQuery(this.searchAttendeeOptionsQueryParams)
    // await this.queryClient.prefetchQuery(this.searchSpeakerOptionsQueryParam)
  }

  getSearchAttendeeOptions() {
    return this.query(this.searchAttendeeOptionsQueryParams).result$
  }

  getSearchSpeakerOptions() {
    return this.query(this.searchSpeakerOptionsQueryParam).result$
  }

  private getUpcomingCallList(cursor: number): Observable<CallListResponse> {
    const params = this.addDemoProfileId({
      cursor,
    })
    return this.httpClient.get<CallListResponse>('/api/calls/upcoming', { params })
  }

  private getOngoingCallList(): Observable<CallWithLatestSession[]> {
    return this.httpClient.get<CallWithLatestSession[]>('/api/calls/ongoing')
  }

  private getPastCallList(cursor: number, callType: PastCallsType, showHiddenCalls: boolean): Observable<CallListResponse> {
    const params = this.addDemoProfileId({
      cursor,
      callType,
      showHiddenCalls,
    })
    return this.httpClient.get<CallListResponse>('/api/calls/past', { params })
  }

  search(filters: CallFilters): Observable<QueryObserverResult<SearchResponse>> {
    return this.query<SearchResponse>({
      queryKey: queryKeys.search.results.byFilters(filters),
      queryFn: () => this.searchCalls(filters),
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    }).result$
  }

  private searchCalls(filters: CallFilters): Observable<SearchResponse> {
    const data = this.addDemoProfileId({
      organizationIds: filters.guestOrganizationId !== null ? [filters.guestOrganizationId] : [],
      attendees: filters.attendeeId !== null ? [filters.attendeeId] : [],
      startDate: filters.startDate,
      endDate: filters.endDate,
      keyword: filters.query,
      speakers: filters.speakerIds,
      showHiddenCalls: false, // TODO
      showOnlyPastMeetings: filters.showOnlyPastMeetings,
    })
    return this.httpClient.post<SearchResponse>('/api/search/calls', data)
  }

  updateCallAccessLevel(callId: number) {
    return this.mutation({
      mutationFn: ({ sharedUserId, level }: { sharedUserId: number; level: CallAccessLevel }) =>
        this.httpClient.post<Call>(`/api/calls/${callId}/update-share-level-for-shared-user`, { sharedUserId, level }),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
      },
    })
  }

  shareToUser(callId: number) {
    return this.mutation({
      mutationFn: ({ sharedUserId, level, notifyByEmail }: { sharedUserId: number; level: CallAccessLevel; notifyByEmail: boolean }) =>
        this.httpClient.post<CallShare>(`/api/calls/${callId}/share-to-user`, { sharedUserId, level, notifyByEmail }),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
      },
    })
  }

  removeShareMutation(callId: number) {
    return this.mutation({
      mutationFn: (sharedUserId: number) => this.httpClient.delete<void>(`/api/calls/${callId}/shares/${sharedUserId}`, {}),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
      },
    })
  }

  assignSentencesToSpeakerMutation() {
    return this.mutation({
      mutationFn: ({
        callId,
        startSentenceId,
        endSentenceId,
        speakerTag,
      }: {
        callId: number
        startSentenceId: string
        endSentenceId: string
        speakerTag: string
      }) => this.httpClient.post<void>(`/api/calls/${callId}/assign-sentences-to-speaker`, { startSentenceId, endSentenceId, speakerTag }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) }, {})
      },
    })
  }

  addSpeakerMutation() {
    return this.mutation({
      mutationFn: ({
        callId,
        speakerName,
        startSentenceId,
        endSentenceId,
      }: {
        callId: number
        speakerName: string
        startSentenceId: string
        endSentenceId: string
      }) => this.httpClient.post<void>(`/api/calls/${callId}/add-speaker`, { speakerName, startSentenceId, endSentenceId }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) }, {})
      },
    })
  }

  resetSpeakersMutation() {
    return this.mutation({
      mutationFn: ({ callId }: { callId: number }) => this.httpClient.post<void>(`/api/calls/${callId}/reset-speakers`, {}),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) }, {})
      },
    })
  }

  toggleExcludeCallMutation() {
    return this.mutation({
      mutationFn: ({ callId, isSetToJoin }: { callId: number; isSetToJoin: boolean }) => this.httpClient.put(`/api/calls/${callId}/user-data`, { isSetToJoin }),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.list.base })
      },
    })
  }

  deleteCallMutation() {
    return this.mutation({
      mutationFn: (callId: number) => this.httpClient.delete<void>(`/api/calls/${callId}`),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.list.base })
      },
    })
  }

  toggleNotificationSetting() {
    return this.mutation({
      mutationFn: ({ callId, notifyWhenPostProcessingCompletes }: { callId: number; notifyWhenPostProcessingCompletes: boolean }) =>
        this.httpClient.put(`/api/calls/${callId}/user-data`, { notifyWhenPostProcessingCompletes }),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.list.base })
      },
    })
  }

  forceDisableJoiningMutation() {
    return this.mutation({
      mutationFn: (callId: number) => this.httpClient.post<void>(`/api/calls/${callId}/force-disable-joining`, {}),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.list.upcoming })
      },
    })
  }

  toggleCallUserJoinMutation() {
    return this.mutation({
      mutationFn: ({ callId, userId, isSetToJoin }: { callId: number; userId: number; isSetToJoin: boolean }) =>
        this.httpClient.put<void>(`/api/calls/${callId}/user-data/toggle-join`, { userId, isSetToJoin }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
      },
    })
  }

  updateCallUserSupportMutation() {
    return this.mutation({
      mutationFn: ({
        callId,
        userId,
        hasWeekendDeletionReminderEmailBeenSent,
        isPrimaryCalendar,
      }: {
        callId: number
        userId: number
        hasWeekendDeletionReminderEmailBeenSent?: boolean
        isPrimaryCalendar?: boolean
      }) => this.httpClient.put<void>(`/api/calls/${callId}/user-data/support`, { userId, hasWeekendDeletionReminderEmailBeenSent, isPrimaryCalendar }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
      },
    })
  }

  deleteCallUserAsSupportMutation() {
    return this.mutation({
      mutationFn: ({ callId, userId }: { callId: number; userId: number }) => this.httpClient.delete<void>(`/api/calls/${callId}/call-users/support/${userId}`),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.client.base })
      },
    })
  }

  // One route for either kind of access, so the dialog never has to work out which of the two tables
  // a person is currently in before changing their role.
  setCallAccessMutation() {
    return this.mutation({
      mutationFn: ({ callId, userId, role }: { callId: number; userId: number; role: CallAccessRole }) =>
        this.httpClient.put<void>(`/api/calls/${callId}/access/${userId}`, { role }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.client.base })
      },
    })
  }

  removeCallAccessMutation() {
    return this.mutation({
      mutationFn: ({ callId, userId }: { callId: number; userId: number }) => this.httpClient.delete<void>(`/api/calls/${callId}/access/${userId}`),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.client.base })
      },
    })
  }

  addCallUserAsSupportMutation() {
    return this.mutation({
      mutationFn: ({ callId, userId }: { callId: number; userId: number }) => this.httpClient.post<void>(`/api/calls/${callId}/call-users/support`, { userId }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.client.base })
      },
    })
  }

  addCallUserMutation() {
    return this.mutation({
      mutationFn: ({ callId, userId }: { callId: number; userId: number }) => this.httpClient.put<void>(`/api/calls/${callId}/call-users/${userId}`, {}),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
      },
    })
  }

  removeCallUserMutation() {
    return this.mutation({
      mutationFn: ({ callId, userId }: { callId: number; userId: number }) => this.httpClient.delete<void>(`/api/calls/${callId}/call-users/${userId}`),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
      },
    })
  }

  getClientCalls(
    nameQuery: string | null,
    includeAllStatuses: boolean,
    dateRange: DateRange,
    sortField: string | null,
    sortDirection: string | null,
    page: number,
    pageSize: number
  ) {
    const params = {
      ...(nameQuery ? { nameQuery } : {}),
      ...(includeAllStatuses ? { includeAllStatuses: true } : {}),
      ...(dateRange.startDate !== null ? { startDate: dateRange.startDate.toISOString() } : {}),
      ...(dateRange.endDate !== null ? { endDate: dateRange.endDate.toISOString() } : {}),
      ...(sortField ? { sortField } : {}),
      ...(sortDirection ? { sortDirection } : {}),
      page,
      pageSize,
    }
    return this.query({
      queryKey: queryKeys.calls.client.list(params),
      queryFn: () => this.httpClient.get<ClientCallQueryResult>('/api/calls/client', { params }),
    }).result$
  }

  getRetentionExtensions(callId: number) {
    return this.query({
      queryKey: queryKeys.calls.client.retentionExtensions(callId),
      queryFn: () => this.httpClient.get<CallRetentionExtension[]>(`/api/calls/${callId}/retention-extensions`),
    }).result$
  }

  extendRetentionPeriodMutation() {
    return this.mutation({
      mutationFn: ({ callId, deletionDate, softDeletionDate }: { callId: number; deletionDate: Date; softDeletionDate: Date }) =>
        this.httpClient.post<void>(`/api/calls/${callId}/extend-retention-period`, {
          deletionDate: deletionDate.toISOString(),
          softDeletionDate: softDeletionDate.toISOString(),
        }),
      onSuccess: (_, { callId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.client.base })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
        this.toastService.show('CALLS.EXTEND_RETENTION.SUCCESS')
      },
      onError: () => {
        this.toastService.show('CALLS.EXTEND_RETENTION.FAILURE')
      },
    })
  }

  restoreCallMutation() {
    return this.mutation({
      mutationFn: ({ callId, dates }: { callId: number; dates: { softDeletionDate: Date; deletionDate: Date } | null }) =>
        this.httpClient.post<void>(`/api/calls/${callId}/restore`, {
          ...(dates !== null ? { softDeletionDate: dates.softDeletionDate.toISOString(), deletionDate: dates.deletionDate.toISOString() } : {}),
        }),
      onSuccess: (_, { callId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.client.base })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.list.base })
        this.toastService.show('CALLS.ADMIN.SOFT_DELETION.UNMARK_SUCCESS')
      },
    })
  }

  getRetentionExtensionRequestOptions(callId: number) {
    return this.query({
      queryKey: queryKeys.calls.retentionExtensionRequests.options(callId),
      queryFn: () => this.httpClient.get<RetentionExtensionRequestOptions>(`/api/calls/${callId}/retention-extension-requests/options`),
    }).result$
  }

  requestRetentionExtensionMutation() {
    return this.mutation({
      mutationFn: ({ callId, extensionInDays }: { callId: number; extensionInDays: number }) =>
        this.httpClient.post<PendingRetentionExtensionRequest>(`/api/calls/${callId}/retention-extension-requests`, { extensionInDays }),
      onSuccess: (_, { callId }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.retentionExtensionRequests.options(callId) })
        this.toastService.show('CALLS.RETENTION_EXTENSION_REQUEST.SUCCESS')
      },
      onError: () => {
        this.toastService.show('CALLS.RETENTION_EXTENSION_REQUEST.FAILURE')
      },
    })
  }

  getRetentionExtensionRequest(callId: number, requestId: number) {
    return this.query({
      queryKey: queryKeys.calls.retentionExtensionRequests.byId(callId, requestId),
      queryFn: () => this.httpClient.get<RetentionExtensionRequestReview>(`/api/calls/${callId}/retention-extension-requests/${requestId}`),
    }).result$
  }

  decideRetentionExtensionRequestMutation() {
    return this.mutation({
      mutationFn: ({ callId, requestId, decision }: { callId: number; requestId: number; decision: 'approve' | 'decline' }) =>
        this.httpClient.post<void>(`/api/calls/${callId}/retention-extension-requests/${requestId}/${decision}`, {}),
      onSuccess: (_, { callId, decision }) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.retentionExtensionRequests.base })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.client.base })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
        this.toastService.show(
          decision === 'approve' ? 'CALLS.RETENTION_EXTENSION_REQUEST.APPROVE_SUCCESS' : 'CALLS.RETENTION_EXTENSION_REQUEST.DECLINE_SUCCESS'
        )
      },
      onError: () => {
        this.toastService.show('CALLS.RETENTION_EXTENSION_REQUEST.DECISION_FAILURE')
      },
    })
  }

  private addDemoProfileId(params: any) {
    const demoProfileId = this.demoProfilesService.activeDemoProfileId$.value
    if (demoProfileId === null) {
      return params
    }
    return { ...params, demoProfileId }
  }

  private listenForActiveDemoProfileUpdates() {
    this.demoProfilesService.activeDemoProfileId$.subscribe(() => {
      this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.list.base })
    })
  }

  addAttendeeMutation() {
    return this.mutation({
      mutationFn: (params: { callId: number; data: Omit<AdminAgendaAttendee, 'id'> }) =>
        this.httpClient.post<AdminAgendaAttendee>(`/api/calls/${params.callId}/attendees`, params.data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
      },
    })
  }

  updateAttendeeMutation() {
    return this.mutation({
      mutationFn: (params: { callId: number; data: AdminAgendaAttendee }) =>
        this.httpClient.put<AdminAgendaAttendee>(`/api/calls/${params.callId}/attendees/${params.data.id}`, params.data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
      },
    })
  }

  deleteAttendeeMutation() {
    return this.mutation({
      mutationFn: (params: { callId: number; attendeeId: number }) =>
        this.httpClient.delete<void>(`/api/calls/${params.callId}/attendees/${params.attendeeId}`),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
      },
    })
  }

  updateMetadataMutation() {
    return this.mutation({
      mutationFn: (params: { callId: number; data: Partial<AdminAgendaMetadata> }) =>
        this.httpClient.patch<AdminAgendaMetadata>(`/api/calls/${params.callId}/metadata`, params.data),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
      },
    })
  }

  joinNowMutation() {
    return this.mutation({
      mutationFn: (callId: number) => this.httpClient.post<void>(`/api/calls/${callId}/join-now`, {}),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.list.upcoming })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.list.ongoing })
      },
    })
  }

  createCallAsSupportMutation() {
    return this.mutation({
      mutationFn: (data: SupportCreateCallData) => this.httpClient.post<Call>(`/api/calls/support`, data),
    })
  }

  createLoadTestMutation() {
    return this.mutation({
      mutationFn: (data: LoadTestData) =>
        this.httpClient.post<{ success: boolean; callCount: number; calls: { id: number; name: string }[] }>(`/api/calls/load-test`, data),
    })
  }

  approveMissingAgendaMutation() {
    return this.mutation({
      mutationFn: (callId: number) => this.httpClient.post<Call>(`/api/calls/${callId}/approve-missing-agenda`, {}),
      onSuccess: (_, callId: number) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
      },
    })
  }

  getCallTimeline(callId: number) {
    return this.query<CallTimelineResponse>({
      queryKey: [...queryKeys.calls.byId(callId), 'timeline'],
      queryFn: () => this.httpClient.get<CallTimelineResponse>(`/api/calls/${callId}/timeline`),
    }).result$
  }

  ingestBoardPackMutation() {
    return this.mutation({
      mutationFn: (data: { callId: number; uploadKey: string; fileName: string }) => {
        return this.httpClient.post<{ jobId: number }>(`/api/calls/${data.callId}/ingest-board-pack`, {
          uploadKey: data.uploadKey,
          fileName: data.fileName,
        })
      },
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
        this.toastService.show('CALLS.ADMIN.BOARD_PACK.INGEST_SUCCESS')
      },
    })
  }

  extractParticipantsMutation() {
    return this.mutation({
      mutationFn: (data: { callId: number; uploadKey: string; fileName: string; fileExtension: string }) => {
        return this.httpClient.post<{ jobId: number }>(`/api/calls/${data.callId}/extract-participants`, {
          uploadKey: data.uploadKey,
          fileName: data.fileName,
          fileExtension: data.fileExtension,
        })
      },
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
      },
      onError: () => {
        this.toastService.show('CALL_SETUP.EXTRACT_PARTICIPANTS.FAILED')
      },
    })
  }

  replaceAttendeesSupportMutation() {
    return this.mutation({
      mutationFn: (params: { callId: number; attendees: Omit<AdminAgendaAttendee, 'id'>[] }) =>
        this.httpClient.put<AdminAgendaAttendee[]>(`/api/calls/${params.callId}/attendees-support`, { attendees: params.attendees }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
      },
    })
  }

  extractParticipantsSupportMutation() {
    return this.mutation({
      mutationFn: (data: { callId: number; uploadKey: string; fileName: string; fileExtension: string }) => {
        return this.httpClient.post<{ jobId: number }>(`/api/calls/${data.callId}/extract-participants-support`, {
          uploadKey: data.uploadKey,
          fileName: data.fileName,
          fileExtension: data.fileExtension,
        })
      },
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.support.byId(params.callId) })
      },
      onError: () => {
        this.toastService.show('CALL_SETUP.EXTRACT_PARTICIPANTS.FAILED')
      },
    })
  }
}
